import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
    Box, Typography, Button, TextField, InputAdornment, Select, MenuItem,
    IconButton, Menu, Dialog, Radio, RadioGroup, FormControlLabel, CircularProgress,
    Tooltip, Checkbox,
} from '@mui/material';
import SearchOutlinedIcon from '@material-ui/icons/SearchOutlined';
import MoreVertOutlinedIcon from '@material-ui/icons/MoreVertOutlined';
import CloseIcon from '@material-ui/icons/Close';
import ArrowDropDownOutlinedIcon from '@material-ui/icons/ArrowDropDownOutlined';
import { useAuth } from '../../../context/auth';
import { DQ_PRO_PLAN_KEY, TOAST_TYPES, WORKSPACE_REQUEST_STATUS, LEARNCREATOR_START_PATH } from '../../../utils/constants';
import {
    listProgramRequestsService, updateProgramRequestStatusService, listOrgClassesService, decideUnlockRequestService,
    quoteLicenceRequestService, decideLicenceRequestService,
} from '../../../services/client.service';
import { getCreditBalanceService } from '../../../services/billing.service';
import { getCountryLabel, useGeo } from '../../../utils/geo';
import { useHistory } from 'react-router-dom';
import { toast } from '../../../utils/utils';
import { contactMailtoHref } from '../../../utils/contactEmail';
import { publishLearningProgramService } from '../../../services/learning_programs.service';
import PublishProgramDialogs from '../components/PublishProgramDialogs.comp';
import {getProgramPreviewPath} from "../DQProgramCreator/DQProProgPreviewRedirect.page";
import {
    TargetGroupFields, TARGET_GROUP_SCOPES, validateTargetGroup,
} from '../DQProgramCreator/components/shared/RequestLearningProgramsWizard.comp';

/**
 * Growth Opportunities > Opportunity Pipeline — Partner workspace.
 *
 * Built from reference mockups (the "Open", "In Progress", "Completed" and
 * "Declined" tabs) with sample/mock data — there is no requests API for this
 * feature yet (see PARTNER_WORKSPACE_PENDING in partnerWorkspace.nav.js).
 * Text content is reproduced verbatim from the mockups wherever legible;
 * struck-through text (group sub-captions like "A school or network is
 * waiting — 5 working day target", and the "N days open" / "Suggested N days
 * ago" line under a row's date on open/in-progress rows) was annotated as
 * removed and is not built here — the "Closed .../Declined ..." dates under
 * completed/declined rows were not struck through, so those are kept.
 * Colors/shapes follow this app's own tokens (orange, plainCardSx,
 * established type scale), not the mockup's own style. The row kebab menu is
 * a real control left unwired — no destination exists for it yet. The header
 * button next to search ("remove" on Open/In Progress/Completed, "New
 * opportunity" on Declined in the mockups) was annotated to be removed
 * entirely, not built as a button — so it isn't here.
 *
 * "Review Request" on the Program Request row (Riverside School) opens a
 * build-confirm dialog (Area/Class/Sessions/Time + estimated credit cost,
 * Accept/Decline/Go back), and Decline inside it opens a second dialog to
 * capture a reason — both from reference mockups tagged P1A/"P1" respectively.
 * Per instruction the mockups' "P1" tag means the P1A + P1B pair (not the
 * separate base enterprise_p1 plan), so this whole flow is gated to those two
 * plans — other plans' "Review Request" buttons stay unwired, same as every
 * other row's action button on this page.
 */

const BORDER = 'var(--brand-border, #E5E7EB)';
const MUTED = 'var(--brand-text-muted, #6B7280)';
const TEXT = 'var(--brand-text, #111827)';
const ORANGE = '#FF6900';

// Same key the request inbox uses, so a build started from either screen binds
// to its request when the program is published.
const PENDING_REQUEST_KEY = 'dq_pending_program_request_id';

// Slide 22 states the rule beside its estimate: "30credit x # of session".
const CREDITS_PER_SESSION = 30;


const PAGE_SIZE_OPTIONS = [5, 10, 25, 50];

/* The pager button used across the portal — orange outline and text, not a
   filled pill. Matches ProgramRequests and Manage Classes. */
const navBtnSx = {
    textTransform: 'none', fontSize: '12.5px', color: ORANGE, borderColor: ORANGE,
    '&:hover': { borderColor: '#e55e00', backgroundColor: 'rgba(255,105,0,0.08)' },
    '&.Mui-disabled': { color: ORANGE, borderColor: ORANGE, opacity: 0.4 },
};

/* org_country is stored as an ISO-2 code (AE, AG). Readers want the name. */
const countryName = (code) => getCountryLabel(code) || code || '';

const plainCardSx = {
    border: `1px solid ${BORDER}`,
    borderRadius: '14px',
    boxShadow: '0px 2px 8px rgba(0,0,0,0.04)',
    backgroundColor: 'var(--brand-surface, #ffffff)',
};

const eyebrowSx = {
    fontSize: '11px', fontWeight: 700, letterSpacing: '0.6px',
    textTransform: 'uppercase', color: MUTED,
};

const primaryButtonSx = {
    backgroundColor: ORANGE,
    color: '#fff',
    textTransform: 'none',
    fontWeight: 700,
    fontSize: '13px',
    borderRadius: '50px',
    px: 2.25,
    py: 0.75,
    whiteSpace: 'nowrap',
    '&:hover': { backgroundColor: '#e55e00' },
};

const secondaryButtonSx = {
    borderColor: BORDER,
    color: TEXT,
    textTransform: 'none',
    fontWeight: 700,
    fontSize: '13px',
    borderRadius: '50px',
    px: 2.25,
    py: 0.75,
    whiteSpace: 'nowrap',
    '&:hover': { borderColor: MUTED, backgroundColor: 'var(--brand-bg, #F9FAFB)' },
};

const STATUS_TABS = [
    /*
     * The datahouse drafts. First, because they are the only rows where
     * nothing has been asked of anyone yet — the partner's own move.
     */
    { key: 'ready_to_suggest', label: 'Ready to Suggest', count: 0 },
    { key: 'open', label: 'Open', count: 6 },
    { key: 'in_progress', label: 'In Progress', count: 3 },
    { key: 'completed', label: 'Completed', count: 2 },
    { key: 'declined', label: 'Declined', count: 1 },
];

const TYPE_FILTERS = [
    { key: 'all', label: 'All types' },
    { key: 'unlock', label: 'Unlock' },
    { key: 'program', label: 'Program' },
    { key: 'license', label: 'License' },
    { key: 'credit', label: 'Credit' },
];

const STAT_CARDS = [
    { label: 'Open Opportunities', value: '8', desc: 'Currently requiring action' },
    { label: 'Awaiting Response', value: '3', desc: 'Direct requests needing follow-up' },
    { label: 'Ready to Suggest', value: '2', desc: 'Data-driven, ready to present' },
    { label: 'Blocked', value: '1', desc: 'Waiting on data, credits or capacity' },
    { label: 'Credit Demand', value: '2,550', desc: 'Requested across open opportunities' },
    { label: 'Credit Gap', value: '790', desc: "Beyond the partner's current balance" },
];

/* Type chip — distinguishes the kind of opportunity a row represents. */
const TYPE_CHIP_STYLES = {
    unlock: { bg: 'rgba(124, 92, 214, 0.1)', color: '#6D45C7', label: 'Upgrade Request' },
    credit: { bg: 'rgba(255, 105, 0, 0.1)', color: '#B14E00', label: 'Credit Request' },
    license: { bg: 'rgba(30, 136, 229, 0.1)', color: '#1565C0', label: 'License Request' },
    program: { bg: 'rgba(107, 114, 128, 0.1)', color: '#4B5563', label: 'Program Request' },
    growth: { bg: 'rgba(214, 92, 150, 0.1)', color: '#B03E72', label: 'Growth Opportunity' },
};

/* Status chip — every value the legend calls out, not just the ones in sample rows. */
const STATUS_CHIP_STYLES = {
    awaiting_response: { bg: 'rgba(217, 119, 6, 0.1)', color: '#B45309', label: 'Awaiting Response' },
    under_review: { bg: 'rgba(107, 114, 128, 0.1)', color: '#4B5563', label: 'Under Review' },
    /*
     * Drafted by the datahouse from assessment results and waiting on the
     * partner. Distinct from `suggested` below, which is the same row AFTER
     * it has been sent and the school is considering it.
     */
    ready_to_suggest: { bg: 'rgba(27, 107, 85, 0.1)', color: '#1B6B55', label: 'Ready to Suggest' },
    proposal_sent: { bg: 'rgba(37, 99, 235, 0.1)', color: '#1D4ED8', label: 'Proposal Sent' },
    building_program: { bg: 'rgba(37, 99, 235, 0.1)', color: '#1D4ED8', label: 'Building Program' },
    credits_pending: { bg: 'rgba(217, 119, 6, 0.1)', color: '#B45309', label: 'Credits Pending' },
    suggested: { bg: 'rgba(13, 148, 136, 0.1)', color: '#0D9488', label: 'Suggested' },
    /*
     * ML has finished the content and the partner has not published yet.
     * Distinct from building_program, which is the same request while ML is
     * still generating, and from completed, which is after delivery.
     */
    ready_to_publish: { bg: 'rgba(21, 128, 61, 0.12)', color: '#15803D', label: 'Ready to Publish' },
    /*
     * Published, but not linked to the school yet (Ian, 5 Oct 2026). Publishing
     * only makes the program ready to link — the school sees it as Live, and
     * the request counts as Completed, once the partner links it in My Schools.
     */
    ready_to_link: { bg: 'rgba(217, 119, 6, 0.1)', color: '#B45309', label: 'Ready to Link' },
    completed: { bg: 'rgba(22, 163, 74, 0.1)', color: '#15803D', label: 'Completed' },
    declined: { bg: 'rgba(220, 38, 38, 0.1)', color: '#B91C1C', label: 'Declined' },
};

/**
 * What each status means, for the chip tooltip.
 *
 * A declined row overrides this with the reason the school was actually
 * given — that is more useful than the generic sentence.
 */
const STATUS_HINTS = {
    awaiting_response: 'The school is waiting on you to review this request.',
    ready_to_suggest:  'Assessment results are in — a program can be suggested.',
    suggested:         'You suggested this program. The school has not answered yet.',
    under_review:      'The school asked for changes before going ahead.',
    building_program:  'You accepted this and the build is under way.',
    ready_to_publish:  'Content generation is finished. Publishing delivers it to the school.',
    ready_to_link:     'Published. Link it to the school in My Schools — the school sees it as Live once it is linked.',
    completed:         'The program was delivered to the school.',
    declined:          'This request was declined.',
};

/*
 * Rows come from the API — see loadRows below. The mock TAB_GROUPS that seeded
 * this page while no endpoint existed has been removed: every row, count and
 * figure now derives from real WorkspaceRequest records.
 */


/* Sample content for the "Review Request" build-confirm dialog — the mockup's
 * own fixed example (Area/Class/Sessions/Time, 30 credits × sessions × the 2
 * classes in "7A, 7B" = 300). Only "Number of sessions" is interactive
 * (recomputes the estimate); the rest match the mockup as fixed values since
 * there's no per-row class/session breakdown to build this from yet. */
const REVIEW_REQUEST_DEFAULTS = {
    area: 'Building ICT Literacy',
    classGroup: 'Grade 7 · 7A, 7B',
    classCount: 2,
    creditsPerSessionPerClass: 30,
    // Every count the school can ask for and the API accepts (1-10), not four
    // presets — the partner could not choose 1, 2, 4, 6, 7 or 9 (bug deck slide 144).
    sessionOptions: Array.from({ length: 10 }, (_, i) => i + 1),
    defaultSessions: 5,
};

/**
 * Every session is taught at 30-45 minutes, so the length is printed and never
 * chosen (bug sheet LA-PW-02Oct-1) — the same wording the school's request
 * wizard uses.
 */
const SESSION_LENGTH = '30–45 minutes';

const DECLINE_REASONS = [
    { value: 'capacity', label: 'Not enough license capacity right now' },
    { value: 'out_of_scope', label: 'Out of scope for this cycle' },
    { value: 'other', label: 'Other' },
];

const TypeChip = ({ type }) => {
    const style = TYPE_CHIP_STYLES[type];
    return (
        <Box sx={{
            display: 'inline-flex', alignItems: 'center', px: 1.25, py: 0.4,
            borderRadius: '50px', backgroundColor: style.bg,
        }}>
            <Typography sx={{ fontSize: '11.5px', fontWeight: 700, color: style.color }}>
                {style.label}
            </Typography>
        </Box>
    );
};

const StatusChip = ({ status }) => {
    const style = STATUS_CHIP_STYLES[status];
    return (
        <Box sx={{
            display: 'inline-flex', alignItems: 'center', px: 1.25, py: 0.4,
            borderRadius: '50px', backgroundColor: style.bg, whiteSpace: 'nowrap',
        }}>
            <Typography sx={{ fontSize: '11.5px', fontWeight: 700, color: style.color }}>
                {style.label}
            </Typography>
        </Box>
    );
};

/**
 * The row overflow menu — BTN-1139 to BTN-1142.
 *
 * Every item used to be `onClick={() => setAnchorEl(null)}`: the menu opened,
 * you picked something, and the only thing that happened was the menu closing.
 *
 * Each item now carries its registry description as a tooltip and calls back.
 * 'Contact school' is the one with no in-app destination — the registry says
 * "External — contact school/network" — so it opens the school contact's
 * mail client rather than pretending to route somewhere.
 */
const KEBAB_ITEMS = {
    'Approve unlock':  'Approve the eligible unlock request and continue the entitlement process.',
    'Approve licences': 'Assign the requested learner licences to the school now.',
    // Not in the slide's menu: the partner taking on a request it cannot grant yet.
    'Accept request':  'Take this request on. Approve it once your workspace holds the capability.',
    'View school':     'Open the requesting school or network record.',
    'Contact school':  'Email the school or network contact.',
    'Decline':         'Decline this request, with a reason the school will see.',
};

/*
 * "Contact School" (ACTION column) and "Contact school" (kebab) — both a native
 * mailto link, see utils/contactEmail. A suggestion carries no requester — the
 * datahouse wrote it — so the school's own contact address is the fallback.
 * With no address at all, the plain click handler says so instead.
 */
const contactSchoolEmail = (row) => row?.raw?.requested_by_email || row?.raw?.org_contact_email || '';
const contactSchoolLink = (row) => ({
    component: 'a',
    href: contactMailtoHref(contactSchoolEmail(row), `Your ${row.title} request`),
});

const RowKebabMenu = ({ row, onMenuAction }) => {
    const [anchorEl, setAnchorEl] = useState(null);
    /*
     * Decline is offered only while the request is still open.
     *
     * It used to appear on every row, including ones already declined — so a
     * declined request could be declined again, which overwrote the stored
     * reason and sent the school a SECOND decline email. On a delivered row it
     * was worse: the school already had the program.
     */
    // A published program (linked or still to link) is delivered work too.
    const isTerminal = row.status === 'declined' || row.status === 'completed' || row.status === 'ready_to_link';
    const isUnlock = row.type === 'unlock';
    const isLicence = row.type === 'license';
    const menuItems = [
        ...(isUnlock && !isTerminal ? ['Approve unlock'] : []),
        ...(isLicence && !isTerminal ? ['Approve licences'] : []),
        ...(isUnlock && row.status === 'awaiting_response' ? ['Accept request'] : []),
        'View school',
        'Contact school',
        ...(isTerminal ? [] : ['Decline']),
    ];
    const pick = (item) => {
        setAnchorEl(null);
        onMenuAction && onMenuAction(item, row);
    };
    return (
        <>
            <IconButton size="small" onClick={(e) => setAnchorEl(e.currentTarget)}>
                <MoreVertOutlinedIcon sx={{ fontSize: 20, color: MUTED }} />
            </IconButton>
            <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={() => setAnchorEl(null)}>
                {menuItems.map((item) => {
                    const link = item === 'Contact school' && contactSchoolEmail(row) ? contactSchoolLink(row) : null;
                    /*
                     * Approve unlock the partner cannot grant yet — its own workspace
                     * lacks the capability, or the school sits inside a network. Kept
                     * in the menu (the slide lists it) but muted, with the reason as
                     * its tooltip; picking it explains rather than failing silently.
                     */
                    const blocked = item === 'Approve unlock' && !row.canApprove;
                    return (
                        <Tooltip key={item} title={(blocked && row.approveBlockedReason) || KEBAB_ITEMS[item] || ''} placement="left" arrow>
                            <MenuItem
                                sx={{ fontSize: '13.5px', ...(blocked ? { color: MUTED } : {}) }}
                                {...(link
                                    ? { ...link, onClick: () => setAnchorEl(null) }
                                    : { onClick: () => pick(item) })}
                            >
                                {item}
                            </MenuItem>
                        </Tooltip>
                    );
                })}
            </Menu>
        </>
    );
};

const dialogPaperSx = {
    borderRadius: '16px',
    border: `1px solid ${BORDER}`,
    boxShadow: '0px 8px 24px rgba(0,0,0,0.08)',
};

/**
 * Slide 20 — the partner reviews a school's drafted suggestions and sends them.
 *
 * STRUCTURE from slide 20 (PW-P1-Growth-Pipeline-SuggestProgram):
 *   header        status chip · School · Country
 *   Program contents  one row per suggestion — checkbox · topic · detail line ·
 *                 credits on the right
 *   footer        "N sessions across M modules" · balance line · total credits ·
 *                 Send suggestion
 *
 * STYLE from this portal, not from the slide: StatusChip, the orange MUI
 * Checkbox, 13.5px / 12px row type, 10px-radius bordered list and the pill
 * primary button — the same pattern as BulkPublishSchoolsDialog.
 *
 * ONE ROW — the suggestion the partner clicked. Listing the school's other
 * drafts here as well repeated the table behind the dialog and made the two
 * counts look like they disagreed.
 *
 * SESSIONS are set by the partner here — the datahouse does not provide them,
 * and PG-01 prints the session count. The row opens on slide 20's 5 sessions.
 * Session LENGTH is not asked for (Ian, 17 Sep: users no longer choose it; the
 * content per session is fixed by LearnCreator). CREDITS follow the platform's
 * rule — credits per session × sessions, as on the Credit Demand card.
 *
 * EDIT COHORT opens the target-group popup from SW slide 14 (Ian, 17 Sep) —
 * the request wizard's own Group type + Grade / Class fields.
 *
 * The footer reads as the slide does: "N sessions across M modules", then the
 * workspace's live credit balance and what is left after this send — or how
 * many short, in the same words as the Credit Gap card.
 */
const SuggestProgramDialog = ({
    open, onClose, onSend, sending = false,
    row, drafts = [], checkedIds, onToggle, loadingDrafts = false, creditBalance,
    // edit cohort: id -> { grade_level, class_name, label }, and the popup opener.
    cohorts, onEditCohort,
    // sessions per row: id -> { sessions }, and the setter.
    plans, onPlanChange,
}) => {
    const head = row?.raw || {};
    const list = drafts.length ? drafts : (row ? [row] : []);
    const isChecked = (d) => Boolean(checkedIds && checkedIds.has(d.id));
    const picked = list.filter(isChecked);

    const learnersOf = (d) => Number(d.raw?.quantity) || 0;
    // The partner's choice, else what the row carries, else slide 20's 5.
    const planOf = (d) => (plans && plans.get ? plans.get(d.id) : null) || {};
    const sessionsOf = (d) => Number(planOf(d).sessions) || Number(d.raw?.num_sessions) || 5;
    const creditsOf  = (d) => sessionsOf(d) * CREDITS_PER_SESSION;

    const pickedSessions = picked.reduce((n, d) => n + sessionsOf(d), 0);
    const pickedCredits  = picked.reduce((n, d) => n + creditsOf(d), 0);

    const hasBalance = creditBalance !== null && creditBalance !== undefined;
    const left = hasBalance ? creditBalance - pickedCredits : null;

    const toggle = (id) => { if (!sending && onToggle) onToggle(id); };

    return (
        <Dialog open={open} onClose={sending ? undefined : onClose} maxWidth="md" fullWidth
                PaperProps={{ sx: dialogPaperSx }}>

            {/* ── Header ──────────────────────────────────────────────────── */}
            <Box sx={{
                display: 'flex', alignItems: 'center', gap: 1.5,
                px: { xs: 2.5, sm: 3 }, py: 2,
                borderBottom: `1px solid ${BORDER}`,
            }}>
                <StatusChip status="ready_to_suggest" />
                <Typography sx={{ fontSize: '14px', color: MUTED, flex: 1, minWidth: 0 }} noWrap>
                    {[row?.org || head.school_name, countryName(head.org_country)].filter(Boolean).join(' · ')}
                </Typography>
                <IconButton size="small" onClick={onClose} disabled={sending} aria-label="Close" sx={{ color: MUTED }}>
                    <CloseIcon style={{ fontSize: 20 }} />
                </IconButton>
            </Box>

            {/* ── Program contents ────────────────────────────────────────── */}
            <Box sx={{ px: { xs: 2.5, sm: 3 }, pt: 3, pb: 2.5 }}>
                <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', mb: 1.5 }}>
                    <Typography sx={{ fontSize: '15px', fontWeight: 800, color: TEXT }}>
                        Program contents
                    </Typography>
                    {loadingDrafts && <CircularProgress size={14} sx={{ color: ORANGE }} />}
                </Box>

                <Box sx={{ border: `1px solid ${BORDER}`, borderRadius: '10px', overflow: 'hidden', maxHeight: '46vh', overflowY: 'auto' }}>
                    {list.map((d, i) => {
                        const r = d.raw || {};
                        const on = isChecked(d);
                        const sessions = sessionsOf(d);
                        const chosen = cohorts && cohorts.get ? cohorts.get(d.id) : null;
                        const scope = chosen
                            ? chosen.label
                            : ([r.grade_level, r.class_name].filter(Boolean).join(' · ') || 'Whole School');
                        const narrowed = chosen ? Boolean(chosen.grade_level || chosen.class_name) : Boolean(r.grade_level || r.class_name);
                        // The assessed figure describes the whole school only.
                        const learners = narrowed ? 0 : learnersOf(d);
                        const detail = [
                            `${sessions} session${sessions === 1 ? '' : 's'}`,
                            scope,
                            learners ? `${learners.toLocaleString()} ${r.quantity_unit || 'learners'}` : null,
                        ].filter(Boolean).join(' · ');

                        return (
                            <Box
                                key={d.id}
                                onClick={() => toggle(d.id)}
                                sx={{
                                    display: 'flex', alignItems: 'center', gap: 1.5,
                                    px: 1.5, py: 1.25,
                                    borderTop: i === 0 ? 'none' : `1px solid ${BORDER}`,
                                    backgroundColor: on ? 'transparent' : 'rgba(17,24,39,0.025)',
                                    cursor: sending ? 'default' : 'pointer',
                                }}
                            >
                                <Checkbox
                                    checked={on}
                                    disabled={sending}
                                    onClick={(e) => e.stopPropagation()}
                                    onChange={() => toggle(d.id)}
                                    inputProps={{ 'aria-label': r.topic || 'Suggestion' }}
                                    sx={{ p: 0.5, color: BORDER, '&.Mui-checked': { color: ORANGE } }}
                                />

                                <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                                    <Tooltip title={r.gap_label || ''} placement="top-start" arrow disableHoverListener={!r.gap_label}>
                                        <Typography sx={{ fontSize: '13.5px', fontWeight: 700, color: on ? TEXT : MUTED }}>
                                            {r.topic || '—'}
                                        </Typography>
                                    </Tooltip>
                                    <Typography sx={{ fontSize: '12px', color: MUTED }}>
                                        {detail}
                                    </Typography>
                                </Box>

                                {/* Number of sessions — the only size the partner sets. */}
                                <Select
                                    size="small"
                                    value={sessions}
                                    disabled={sending}
                                    onClick={(e) => e.stopPropagation()}
                                    onChange={(e) => onPlanChange && onPlanChange(d.id, { sessions: Number(e.target.value) })}
                                    sx={{ minWidth: 120, fontSize: '13px', flexShrink: 0, backgroundColor: 'var(--brand-surface, #fff)' }}
                                >
                                    {REVIEW_SESSION_COUNTS.map((n) => (
                                        <MenuItem key={n} value={n} sx={{ fontSize: '13px' }}>{n} session{n === 1 ? '' : 's'}</MenuItem>
                                    ))}
                                </Select>

                                {/* SW slide 14's target-group popup (Ian, 17 Sep). */}
                                <Button
                                    size="small"
                                    disabled={sending}
                                    onClick={(e) => { e.stopPropagation(); if (onEditCohort) onEditCohort(d); }}
                                    sx={{ textTransform: 'none', fontSize: '12.5px', fontWeight: 600, color: ORANGE, minWidth: 0, px: 1, flexShrink: 0 }}
                                >
                                    edit cohort
                                </Button>

                                <Box sx={{ textAlign: 'right', flexShrink: 0, minWidth: 64 }}>
                                    <Typography sx={{ fontSize: '15px', fontWeight: 800, color: on ? TEXT : MUTED, lineHeight: 1.2 }}>
                                        {creditsOf(d).toLocaleString()}
                                    </Typography>
                                    <Typography sx={{ ...eyebrowSx, fontSize: '10px' }}>Credits</Typography>
                                </Box>
                            </Box>
                        );
                    })}
                </Box>
            </Box>

            {/* ── Footer ──────────────────────────────────────────────────── */}
            <Box sx={{
                px: { xs: 2.5, sm: 3 }, py: 2.25,
                borderTop: `1px solid ${BORDER}`,
                display: 'flex', flexDirection: { xs: 'column', sm: 'row' },
                alignItems: { xs: 'stretch', sm: 'center' }, gap: 2,
            }}>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography sx={{ fontSize: '14px', fontWeight: 700, color: TEXT }}>
                        {`${pickedSessions.toLocaleString()} session${pickedSessions === 1 ? '' : 's'} across ${picked.length} module${picked.length === 1 ? '' : 's'}`}
                    </Typography>
                    {hasBalance && (
                        <Typography sx={{ fontSize: '12px', color: MUTED, mt: 0.25 }}>
                            {left < 0
                                ? `${creditBalance.toLocaleString()} available · ${Math.abs(left).toLocaleString()} short`
                                : `${creditBalance.toLocaleString()} available · ${left.toLocaleString()} left after this send`}
                        </Typography>
                    )}
                </Box>

                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 3 }}>
                    <Box sx={{ textAlign: 'right' }}>
                        <Typography sx={{ fontSize: '26px', fontWeight: 800, color: TEXT, lineHeight: 1 }}>
                            {pickedCredits.toLocaleString()}
                        </Typography>
                        <Typography sx={{ ...eyebrowSx, fontSize: '10px', mt: 0.25 }}>Credits</Typography>
                    </Box>
                    {/* Disabled with nothing ticked, and while the writes are in
                        flight so one click cannot send the same draft twice. */}
                    <Button
                        sx={{ ...primaryButtonSx, fontSize: '14px', px: 3, py: 1,
                              '&.Mui-disabled': { backgroundColor: ORANGE, color: '#fff', opacity: 0.45 } }}
                        onClick={onSend}
                        disabled={sending || picked.length === 0}
                    >
                        {sending
                            ? 'Sending…'
                            : picked.length > 1 ? `Send ${picked.length} suggestions` : 'Send suggestion'}
                    </Button>
                </Box>
            </Box>
        </Dialog>
    );
};

/**
 * Slide 21 — shown after a suggestion is sent.
 *
 * schoolName comes from the row that was actually sent. It used to read a
 * fixed mockup value, so every partner was told they had written to
 * "Riverside International" whichever school they had picked.
 */
const SuggestionSentDialog = ({ open, onClose, schoolName, count = 1 }) => (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth PaperProps={{ sx: dialogPaperSx }}>
        <Box sx={{ p: 4, textAlign: 'center' }}>
            <Typography sx={{ fontSize: '15px', color: TEXT, lineHeight: 1.7, mb: 3 }}>
                Great! We've shared your learning program {count > 1 ? `recommendations (${count})` : 'recommendation'} with{' '}
                <Box component="span" sx={{ fontWeight: 800 }}>{schoolName || 'the school'}</Box>.
                As soon as they approve it, we'll let you know so you can start building their program.
            </Typography>
            <Button sx={primaryButtonSx} onClick={onClose}>
                Ok
            </Button>
        </Box>
    </Dialog>
);

/**
 * "edit cohort" — PW slide 20.
 *
 * Ian (17 Sep): show only the target-group part of SW slide 14's "How should it
 * run?" popup — Group type, then Grade / Class — rather than controls beside the
 * session count. So this renders the request wizard's own TargetGroupFields and
 * nothing else. A suggestion is always for ONE school, so the school scopes are
 * offered (Whole school / Grade(s) / Class(es)), with that school's own grades
 * and classes.
 */
const EditCohortDialog = ({ open, schoolName, topic, initial, grades = [], classes = [], onClose, onSave }) => {
    const [detail, setDetail] = useState({ groupType: '', groupValue: '', groupInstituteCode: '', groupValues: [], groupInstituteCodes: [] });

    useEffect(() => {
        if (!open) return;
        const { SCOPE_SCHOOL, SCOPE_GRADE, SCOPE_CLASS } = TARGET_GROUP_SCOPES;
        // Open on what the row already targets.
        setDetail({
            groupType: initial?.class_name ? SCOPE_CLASS.label : initial?.grade_level ? SCOPE_GRADE.label : SCOPE_SCHOOL.label,
            groupValue: initial?.class_name || initial?.grade_level || '',
            groupInstituteCode: initial?.institute_code || '',
            groupValues: [], groupInstituteCodes: [],
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    const { SCOPE_SCHOOL, SCOPE_GRADE, SCOPE_CLASS } = TARGET_GROUP_SCOPES;
    const wholeSchool = detail.groupType === SCOPE_SCHOOL.label;
    const choices = detail.groupType === SCOPE_CLASS.label ? classes
        : detail.groupType === SCOPE_GRADE.label ? grades
        : [];
    const errors = validateTargetGroup(detail, wholeSchool);
    const valid = Boolean(detail.groupType) && !errors.groupType && !errors.groupValue;

    const save = () => {
        if (!valid) return;
        const isClass = detail.groupType === SCOPE_CLASS.label;
        const isGrade = detail.groupType === SCOPE_GRADE.label;
        onSave({
            grade_level: isGrade ? detail.groupValue : '',
            class_name: isClass ? detail.groupValue : '',
            label: wholeSchool ? 'Whole School' : detail.groupValue,
        });
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth PaperProps={{ sx: dialogPaperSx }}>
            <Box sx={{ px: 3, pt: 2.5, pb: 1 }}>
                <Typography sx={{ fontSize: '17px', fontWeight: 800, color: TEXT }}>Edit cohort</Typography>
                <Typography sx={{ fontSize: '13px', color: MUTED, mt: 0.25, mb: 2 }}>
                    {[schoolName, topic].filter(Boolean).join(' · ')}
                </Typography>
                <TargetGroupFields
                    detail={detail}
                    onChange={setDetail}
                    groupChoices={choices}
                    isNetwork={false}
                    errors={detail.groupType ? errors : {}}
                />
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1.5, px: 3, pb: 2.5 }}>
                <Button sx={secondaryButtonSx} onClick={onClose}>Cancel</Button>
                <Button
                    sx={{ ...primaryButtonSx, '&.Mui-disabled': { backgroundColor: ORANGE, color: '#fff', opacity: 0.45 } }}
                    disabled={!valid}
                    onClick={save}
                >
                    Save
                </Button>
            </Box>
        </Dialog>
    );
};

/**
 * Review the changes a school asked for, and send the suggestion back — PG-05.
 *
 * The pack's own words set the scope: "Review the revised scope and send it
 * back when you are happy with it." So this shows what the school asked for
 * (their text, the group they chose, any advanced options) and lets the
 * partner settle the sessions before sending it back. Sending it back offers it
 * to the school again (status 0) and sends PG-01, exactly as the first send.
 *
 * Area and group are the school's and are shown, not edited: the area is locked
 * on SW-47, and the group is what the school just chose.
 *
 * Kept below slide 21's dialog on purpose — above it sits the Suggest dialog,
 * which is regenerated as one block.
 */
const REVIEW_SESSION_COUNTS = Array.from({ length: 10 }, (_, i) => i + 1);

/** One labelled line of the review — declared once, so its inputs keep their state. */
const ReviewLine = ({ label, children }) => (
    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '160px minmax(0, 1fr)' }, gap: 1, py: 1.25, borderBottom: `1px solid ${BORDER}` }}>
        <Typography sx={{ fontSize: '13px', fontWeight: 700, color: TEXT }}>{label}</Typography>
        <Box sx={{ fontSize: '14px', color: TEXT }}>{children}</Box>
    </Box>
);

/*
 * readOnly — the school asked for changes to a request the partner has already
 * ACCEPTED (7 Oct 2026). The mail says "Review the changes" and the row only
 * carried a one-line note. Nothing is sent back in that case: the partner reads
 * what was asked, then goes on to the build (onContinue = Update Progress).
 */
const ReviewChangesDialog = ({ open, row, onClose, onSendBack, sending = false, grades = [], classes = [], readOnly = false, onContinue }) => {
    const r = row?.raw || {};
    const [sessions, setSessions] = useState(5);
    /*
     * The cohort, with the same fields as "edit cohort" (slide 20) and the
     * school's own Request changes: the partner may settle the group as well as
     * the sessions before sending the suggestion back. Opens on what the school
     * chose.
     */
    const { SCOPE_SCHOOL, SCOPE_GRADE, SCOPE_CLASS } = TARGET_GROUP_SCOPES;
    const startDetail = () => ({
        groupType: r.class_name ? SCOPE_CLASS.label : r.grade_level ? SCOPE_GRADE.label : SCOPE_SCHOOL.label,
        groupValue: r.class_name || r.grade_level || '',
        groupInstituteCode: r.institute_code || '',
        groupValues: [], groupInstituteCodes: [],
    });
    const [detail, setDetail] = useState(startDetail);

    useEffect(() => {
        if (!open) return;
        // The school's count if it set one; slide 47's starting value if not.
        setSessions(Number(r.num_sessions) || 5);
        setDetail(startDetail());
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, row]);

    const wholeSchool = detail.groupType === SCOPE_SCHOOL.label;
    const choices = detail.groupType === SCOPE_CLASS.label ? classes
        : detail.groupType === SCOPE_GRADE.label ? grades
        : [];
    const errors = validateTargetGroup(detail, wholeSchool);
    const groupValid = Boolean(detail.groupType) && !errors.groupType && !errors.groupValue;

    return (
        <Dialog open={open} onClose={sending ? undefined : onClose} maxWidth="sm" fullWidth PaperProps={{ sx: dialogPaperSx }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, px: 3, py: 2, borderBottom: `1px solid ${BORDER}` }}>
                <StatusChip status={readOnly ? (row?.status || 'building_program') : 'under_review'} />
                <Typography sx={{ fontSize: '14px', color: MUTED, flex: 1, minWidth: 0 }} noWrap>
                    {[row?.org || r.school_name, countryName(r.org_country)].filter(Boolean).join(' · ')}
                </Typography>
                <IconButton size="small" onClick={onClose} disabled={sending} aria-label="Close" sx={{ color: MUTED }}>
                    <CloseIcon style={{ fontSize: 20 }} />
                </IconButton>
            </Box>

            <Box sx={{ px: 3, pt: 2.5, pb: 1 }}>
                <Typography sx={{ fontSize: '15px', fontWeight: 800, color: TEXT, mb: 0.5 }}>
                    Changes requested
                </Typography>
                {!readOnly && (
                    <Typography sx={{ fontSize: '13px', color: MUTED, mb: 2 }}>
                        Review the revised scope and send it back when you are happy with it.
                    </Typography>
                )}

                <Box sx={{ backgroundColor: 'rgba(255,105,0,0.05)', borderLeft: `3px solid ${ORANGE}`, borderRadius: '6px', px: 2, py: 1.5, mb: 1.5 }}>
                    <Typography sx={{ ...eyebrowSx, fontSize: '10px', mb: 0.5 }}>What they asked for</Typography>
                    <Typography sx={{ fontSize: '14px', color: TEXT, whiteSpace: 'pre-wrap' }}>
                        {r.requested_changes || '—'}
                    </Typography>
                </Box>

                <ReviewLine label="Area">{r.topic || '—'}</ReviewLine>
                {readOnly && (
                    <ReviewLine label={groupIsSchool(r) ? 'School' : 'Class / group'}>{row?.qtyContext || 'Whole School'}</ReviewLine>
                )}
                {!readOnly && <ReviewLine label="Class / group">
                    {/* One column, so both selects fit the value column; minWidth 0
                        lets a long class name truncate instead of widening the dialog. */}
                    <Box sx={{ minWidth: 0, '& > div': { mb: 0, gridTemplateColumns: '1fr' }, '& > div > *': { minWidth: 0 } }}>
                        <TargetGroupFields
                            detail={detail}
                            onChange={setDetail}
                            groupChoices={choices}
                            isNetwork={false}
                            errors={detail.groupType ? errors : {}}
                        />
                    </Box>
                </ReviewLine>}
                {r.school_subject && <ReviewLine label="Topic / theme">{r.school_subject}</ReviewLine>}
                {r.local_sensitivities && <ReviewLine label="Sensitivities">{r.local_sensitivities}</ReviewLine>}
                {readOnly && (
                    <ReviewLine label="Number of sessions">{row?.qty || '—'}</ReviewLine>
                )}
                {!readOnly && <ReviewLine label="Number of sessions">
                    <Select size="small" value={sessions} onChange={(e) => setSessions(Number(e.target.value))} sx={{ fontSize: '14px', minWidth: 160 }}>
                        {REVIEW_SESSION_COUNTS.map((n) => (
                            <MenuItem key={n} value={n}>{n} session{n === 1 ? '' : 's'}</MenuItem>
                        ))}
                    </Select>
                </ReviewLine>}
            </Box>

            <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 1.5, px: 3, py: 2.25 }}>
                <Button sx={secondaryButtonSx} onClick={onClose} disabled={sending}>Go back</Button>
                {readOnly && (
                    <Button sx={primaryButtonSx} onClick={() => onContinue && onContinue()}>Update Progress</Button>
                )}
                {!readOnly && <Button
                    sx={{ ...primaryButtonSx, '&.Mui-disabled': { backgroundColor: ORANGE, color: '#fff', opacity: 0.45 } }}
                    disabled={sending || !groupValid}
                    onClick={() => onSendBack && onSendBack({
                        num_sessions: sessions,
                        grade_level: detail.groupType === SCOPE_GRADE.label ? detail.groupValue : '',
                        class_name: detail.groupType === SCOPE_CLASS.label ? detail.groupValue : '',
                    })}
                >
                    {sending ? 'Sending…' : 'Send back to school'}
                </Button>}
            </Box>
        </Dialog>
    );
};

const dropdownFieldSx = {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    border: `1px solid ${BORDER}`, borderRadius: '10px', px: 1.75, py: 1.1,
};

/**
 * Confirm Build Request from School — PW slide 22.
 *
 * Every field is seeded from the REQUEST. It used to show
 * REVIEW_REQUEST_DEFAULTS, so a school that asked for 2 x 30 min was shown
 * "5 sessions / 45 minutes" — the partner read numbers the school never sent,
 * while the email carried the real ones.
 *
 * Sessions stay editable because the slide says "Adjust anything below". Area
 * and Class / group are the school's own ask and are shown read-only — Area is
 * marked "locked" on the slide, and the group is what the request names. Time
 * per session is fixed (SESSION_LENGTH), so it is read-only too.
 */
/*
 * The group a request is for, naming the network's school as the network's own
 * pop-up does (LA-SNW-13Oct-03, in the PW): a class reads "School - Class", a
 * whole-school request reads the school's name, and a grade keeps its own text.
 * institute_name is the school the request was saved with (empty for a single
 * school or an older request — those keep the text they had).
 */
const groupTextOf = (r = {}, wholeSchool = 'Whole School') => {
    const own = [r.grade_level, r.class_name].filter(Boolean).join(' · ') || wholeSchool;
    const school = r.institute_name || '';
    if (!school) return own;
    if (r.class_name) return `${school} - ${r.class_name}`;
    return r.grade_level ? own : school;
};
// That whole-school request now shows a school, so its label says "School".
const groupIsSchool = (r = {}) => !!r.institute_name && !r.class_name && !r.grade_level;

const ReviewBuildRequestDialog = ({ open, row, onClose, onAccept, onDecline }) => {
    const req = row?.raw || {};
    const [sessions, setSessions] = useState(REVIEW_REQUEST_DEFAULTS.defaultSessions);

    // Re-seed whenever a different row is opened; without this the dialog
    // keeps the previous request's numbers.
    useEffect(() => {
        if (!open) return;
        setSessions(Number(req.num_sessions) || REVIEW_REQUEST_DEFAULTS.defaultSessions);
    }, [open, req.num_sessions]);

    if (!row) return null;

    const areaLabel = req.topic || REVIEW_REQUEST_DEFAULTS.area;
    const groupLabel = groupTextOf(req, 'Whole school');

    /*
     * Slide 22 states the rule beside the figure: "30credit x # of session".
     * It multiplies by sessions only — not by a class count, which the old
     * mock did and which made every estimate twice what the slide shows.
     */
    const estimatedCredits = REVIEW_REQUEST_DEFAULTS.creditsPerSessionPerClass * sessions;

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth PaperProps={{ sx: dialogPaperSx }}>
            <Box sx={{ p: { xs: 3, sm: 4 } }}>
                <Box sx={{
                    display: 'flex', flexDirection: { xs: 'column', sm: 'row' },
                    justifyContent: 'space-between', alignItems: { xs: 'flex-start', sm: 'center' }, gap: 2, mb: 3,
                }}>
                    <Box>
                        <Typography sx={{ fontSize: '19px', fontWeight: 800, color: ORANGE, mb: 0.5 }}>
                            {`Learning Program Requested by ${row.org}`}
                        </Typography>
                        <Typography sx={{ fontSize: '13px', color: MUTED }}>
                            Adjust anything below — changes go back for confirmation.
                        </Typography>
                    </Box>
                    <Box sx={{
                        border: `1px solid ${BORDER}`, borderRadius: '10px', p: 1.75,
                        textAlign: 'center', flexShrink: 0, minWidth: '110px',
                    }}>
                        <Typography sx={{ ...eyebrowSx, fontSize: '10px', mb: 0.5 }}>
                            Estimated Credit Cost
                        </Typography>
                        <Typography sx={{ fontSize: '22px', fontWeight: 800, color: TEXT, lineHeight: 1 }}>
                            {estimatedCredits}
                        </Typography>
                    </Box>
                </Box>

                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Typography sx={{ fontSize: '13px', color: MUTED, width: '110px', flexShrink: 0 }}>Area</Typography>
                        <Box sx={{ ...dropdownFieldSx, flex: 1, backgroundColor: 'var(--brand-bg, #F9FAFB)' }}>
                            <Typography sx={{ fontSize: '14px', color: MUTED }}>{areaLabel}</Typography>
                            <Box sx={{
                                px: 1, py: 0.25, borderRadius: '50px', backgroundColor: 'var(--brand-bg, #F3F4F6)',
                            }}>
                                <Typography sx={{ fontSize: '10.5px', fontWeight: 700, color: MUTED }}>locked</Typography>
                            </Box>
                        </Box>
                    </Box>

                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Typography sx={{ fontSize: '13px', color: MUTED, width: '110px', flexShrink: 0 }}>{groupIsSchool(req) ? 'School' : 'Class / group'}</Typography>
                        {/* Read-only: this is the group the SCHOOL asked for. The
                            chevron that used to sit here implied a dropdown that never
                            opened. */}
                        <Box sx={{ ...dropdownFieldSx, flex: 1, backgroundColor: 'var(--brand-bg, #F9FAFB)' }}>
                            <Typography sx={{ fontSize: '14px', color: TEXT, fontWeight: 700 }}>{groupLabel}</Typography>
                        </Box>
                    </Box>

                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Typography sx={{ fontSize: '13px', color: MUTED, width: '110px', flexShrink: 0 }}>Number of sessions</Typography>
                        <Select
                            size="small"
                            value={sessions}
                            onChange={(e) => setSessions(e.target.value)}
                            IconComponent={ArrowDropDownOutlinedIcon}
                            sx={{
                                flex: 1, fontSize: '14px', fontWeight: 700, color: TEXT,
                                '& .MuiOutlinedInput-notchedOutline': { borderColor: BORDER },
                                '& .MuiSelect-select': { py: 1.1, px: 1.75 },
                            }}
                        >
                            {/* The school's own figure is always offered, even when it
                                is not one of the presets — otherwise the Select falls
                                back to a value nobody chose. */}
                            {[...new Set([...REVIEW_REQUEST_DEFAULTS.sessionOptions, sessions])]
                                .sort((a, b) => a - b)
                                .map((n) => (
                                    <MenuItem key={n} value={n} sx={{ fontSize: '13.5px' }}>
                                        {`${n} session${n === 1 ? '' : 's'}`}
                                    </MenuItem>
                                ))}
                        </Select>
                    </Box>

                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Typography sx={{ fontSize: '13px', color: MUTED, width: '110px', flexShrink: 0 }}>Time per session</Typography>
                        {/* Read-only: a session's length is fixed, not chosen. */}
                        <Box sx={{ ...dropdownFieldSx, flex: 1, backgroundColor: 'var(--brand-bg, #F9FAFB)' }}>
                            <Typography sx={{ fontSize: '14px', color: TEXT, fontWeight: 700 }}>{SESSION_LENGTH}</Typography>
                        </Box>
                    </Box>

                    {/* What the school added for the build (LA-SW,SNW-14Oct-01). */}
                    {req.additional_info && (
                        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                            <Typography sx={{ fontSize: '13px', color: MUTED, width: '110px', flexShrink: 0 }}>Other information</Typography>
                            <Typography sx={{ fontSize: '14px', color: TEXT, flex: 1, whiteSpace: 'pre-wrap' }}>{req.additional_info}</Typography>
                        </Box>
                    )}
                </Box>

                <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1.25, mt: 4 }}>
                    <Button variant="outlined" sx={secondaryButtonSx} onClick={onClose}>
                        Go back
                    </Button>
                    <Button variant="outlined" sx={secondaryButtonSx} onClick={onDecline}>
                        Decline
                    </Button>
                    {/* The adjusted scope travels with the accept. Sending only the
                        credit figure left the edits in this component: the build and
                        the school's email both used the original ask. */}
                    <Button
                        sx={primaryButtonSx}
                        onClick={() => onAccept({ sessions, credits: estimatedCredits })}
                    >
                        Accept
                    </Button>
                </Box>
            </Box>
        </Dialog>
    );
};

/**
 * Approve an unlock request — grants the capability to the school or network
 * now and emails them (RQ-03).
 *
 * COS Readiness Solution is requested as one thing and granted as one of two:
 * Provider-Created Programs (you build the programs, the school links them) or
 * Teacher-Created Programs (the school gets LearnCreator). That choice is the
 * partner's, so it is asked here; Provider-Created is the default.
 */
const ApproveUnlockDialog = ({ open, row, approving, onCancel, onApprove }) => {
    const variants = row?.approveVariants || [];
    const [variant, setVariant] = useState('');
    useEffect(() => { setVariant(variants[0]?.key || ''); }, [row]); // eslint-disable-line react-hooks/exhaustive-deps

    if (!row) return null;
    const capability = row.raw?.requested_capability_label || row.title;

    return (
        <Dialog open={open} onClose={onCancel} maxWidth="xs" fullWidth PaperProps={{ sx: dialogPaperSx }}>
            <Box sx={{ p: { xs: 3, sm: 4 } }}>
                <Typography sx={{ fontSize: '18px', fontWeight: 800, color: TEXT, mb: 0.5 }}>
                    {`Approve · ${row.title}`}
                </Typography>
                <Typography sx={{ fontSize: '13.5px', color: MUTED, mb: 2.5 }}>
                    {row.org}
                </Typography>

                <Typography sx={{ fontSize: '14px', color: TEXT, lineHeight: 1.6, mb: variants.length ? 2 : 0 }}>
                    {row.approveToNetwork
                        /*
                         * A school inside a network: the grant goes to its network
                         * (product owner, 9 Oct 2026), which then decides which of
                         * its schools get it.
                         */
                        ? `${row.approveTargetName || 'Their network'} gets ${capability} as soon as you approve, and is emailed that it is active. The network then decides whether ${row.org} — or any of its other schools — gets it.`
                        : row.raw?.requested_by_label === 'Network Coordinator'
                        // A network's own request: approval stops at the network too.
                        ? `${row.org} gets ${capability} as soon as you approve, and is emailed that it is active. Its schools get it when the network assigns it to them.`
                        : `${row.org} gets ${capability} as soon as you approve, and is emailed that it is active. Everything they have now stays as it is.`}
                </Typography>

                {variants.length > 0 && (
                    <>
                        <Typography sx={{ fontSize: '12px', fontWeight: 700, color: MUTED, mb: 1 }}>
                            Who builds the programs
                        </Typography>
                        <RadioGroup value={variant} onChange={(e) => setVariant(e.target.value)}>
                            {variants.map((v) => (
                                <FormControlLabel
                                    key={v.key}
                                    value={v.key}
                                    control={<Radio size="small" sx={{ color: BORDER, '&.Mui-checked': { color: ORANGE } }} />}
                                    label={<Typography sx={{ fontSize: '14px', color: TEXT }}>{v.label}</Typography>}
                                    sx={{ mb: 0.5 }}
                                />
                            ))}
                        </RadioGroup>
                    </>
                )}

                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 1.25, mt: 3 }}>
                    <Button variant="outlined" sx={secondaryButtonSx} onClick={onCancel} disabled={approving}>
                        Cancel
                    </Button>
                    <Button sx={primaryButtonSx} disabled={approving || (variants.length > 0 && !variant)}
                            onClick={() => onApprove(variant || undefined)}>
                        {approving ? <CircularProgress size={16} sx={{ color: '#fff' }} /> : 'Approve'}
                    </Button>
                </Box>
            </Box>
        </Dialog>
    );
};

/**
 * Approve a licence request — assigns the licences to the school that asked,
 * now, and emails them (CP-01/LIC).
 *
 * The partner may assign a different number than was asked. On a DQ Pro
 * partner plan the licences are converted from DQ Tokens at the plan's prices,
 * so the live quote is shown before the click and Approve is held back when the
 * balance does not cover it; on a legacy plan they are assigned free.
 */
const ApproveLicencesDialog = ({ open, row, approving, onCancel, onApprove }) => {
    const requested = Number(row?.raw?.requested_licences) || 0;
    const [count, setCount] = useState('');
    const [quote, setQuote] = useState(null);
    const [quoting, setQuoting] = useState(false);
    useEffect(() => { setCount(requested ? String(requested) : ''); setQuote(null); }, [row]); // eslint-disable-line react-hooks/exhaustive-deps

    const n = Number(count);
    const valid = Number.isInteger(n) && n >= 1;

    // Re-quote as the number changes; the last answer wins.
    useEffect(() => {
        if (!open || !row || !valid) { setQuote(null); setQuoting(false); return undefined; }
        let live = true;
        setQuoting(true);
        const t = setTimeout(async () => {
            try {
                const res = await quoteLicenceRequestService({ request_id: row.raw?._id, licences: n });
                if (live) setQuote(res?.data || null);
            } catch (e) {
                if (live) setQuote(null);
            } finally {
                if (live) setQuoting(false);
            }
        }, 300);
        return () => { live = false; clearTimeout(t); };
    }, [open, row, count]); // eslint-disable-line react-hooks/exhaustive-deps

    if (!row) return null;
    const q = quote?.quote;
    const blocked = !!(quote?.uses_dqt && q && !q.allowed);
    const fmt = (v) => Number(v || 0).toLocaleString();

    return (
        <Dialog open={open} onClose={approving ? undefined : onCancel} maxWidth="xs" fullWidth PaperProps={{ sx: dialogPaperSx }}>
            <Box sx={{ p: { xs: 3, sm: 4 } }}>
                <Typography sx={{ fontSize: '18px', fontWeight: 800, color: TEXT, mb: 0.5 }}>
                    Approve · Learner licences
                </Typography>
                <Typography sx={{ fontSize: '13.5px', color: MUTED, mb: 2.5 }}>
                    {row.org}
                </Typography>

                <Typography sx={{ fontSize: '14px', color: TEXT, lineHeight: 1.6, mb: 2 }}>
                    {`${row.org} asked for ${requested} learner licence${requested === 1 ? '' : 's'}`}
                    {row.raw?.learner_count ? ` for ${row.raw.learner_count} learners waiting to start` : ''}.
                    {' '}They are assigned as soon as you approve, and the school is emailed that it can publish.
                </Typography>

                <Typography sx={{ fontSize: '12px', fontWeight: 700, color: MUTED, mb: 1 }}>
                    Licences to assign
                </Typography>
                <TextField
                    size="small"
                    type="number"
                    fullWidth
                    value={count}
                    onChange={(e) => setCount(e.target.value)}
                    inputProps={{ min: 1, step: 1 }}
                    error={!!count && !valid}
                    helperText={count && !valid ? 'Enter a whole number of 1 or more.' : ''}
                />

                <Box sx={{ mt: 2, minHeight: 44 }}>
                    {quoting ? (
                        <CircularProgress size={16} />
                    ) : quote && !quote.uses_dqt ? (
                        <Typography sx={{ fontSize: '13px', color: MUTED }}>
                            On your plan these licences are assigned at no DQ Token cost.
                        </Typography>
                    ) : q ? (
                        <>
                            <Typography sx={{ fontSize: '13px', color: TEXT }}>
                                {`Costs ${fmt(q.dqt_cost?.total)} DQ Tokens · you have ${fmt(q.workspace?.before?.dqt)}, ${fmt(q.workspace?.after?.dqt)} after`}
                            </Typography>
                            {blocked && (
                                <Typography sx={{ fontSize: '13px', color: '#B86A00', mt: 0.5 }}>
                                    {q.message}
                                </Typography>
                            )}
                        </>
                    ) : null}
                </Box>

                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 1.25, mt: 2 }}>
                    <Button variant="outlined" sx={secondaryButtonSx} onClick={onCancel} disabled={approving}>
                        Cancel
                    </Button>
                    <Button sx={primaryButtonSx} disabled={approving || quoting || !valid || blocked}
                            onClick={() => onApprove(n)}>
                        {approving ? <CircularProgress size={16} sx={{ color: '#fff' }} /> : 'Approve'}
                    </Button>
                </Box>
            </Box>
        </Dialog>
    );
};

const DeclineReasonDialog = ({ open, row, onKeepRequest, onConfirmDecline }) => {
    const [reason, setReason] = useState(DECLINE_REASONS[0].value);
    /*
     * Choosing "Other" opens a box, and the text is REQUIRED.
     *
     * The reason is quoted verbatim into PG-06 under a "Why" heading, so an
     * empty Other would send the school a blank explanation — worse than no
     * email at all, because it looks like the partner could not be bothered.
     */
    const [otherText, setOtherText] = useState('');
    const isOther = reason === 'other';
    const finalReason = isOther ? otherText.trim() : (DECLINE_REASONS.find((r) => r.value === reason)?.label || reason);
    const canDecline = finalReason.length > 0;

    if (!row) return null;

    return (
        <Dialog open={open} onClose={onKeepRequest} maxWidth="xs" fullWidth PaperProps={{ sx: dialogPaperSx }}>
            <Box sx={{ p: { xs: 3, sm: 4 } }}>
                <Typography sx={{ fontSize: '18px', fontWeight: 800, color: TEXT, mb: 0.5 }}>
                    {`Decline · ${row.title}`}
                </Typography>
                <Typography sx={{ fontSize: '13.5px', color: MUTED, mb: 2.5 }}>
                    {row.org}
                </Typography>

                <Typography sx={{ fontSize: '12px', fontWeight: 700, color: MUTED, mb: 1 }}>
                    Reason (shared with the school)
                </Typography>
                <RadioGroup value={reason} onChange={(e) => setReason(e.target.value)}>
                    {DECLINE_REASONS.map((r) => (
                        <FormControlLabel
                            key={r.value}
                            value={r.value}
                            control={<Radio size="small" sx={{ color: BORDER, '&.Mui-checked': { color: ORANGE } }} />}
                            label={<Typography sx={{ fontSize: '14px', color: TEXT }}>{r.label}</Typography>}
                            sx={{ mb: 0.5 }}
                        />
                    ))}
                </RadioGroup>

                {isOther && (
                    <TextField
                        size="small"
                        fullWidth
                        multiline
                        minRows={2}
                        autoFocus
                        placeholder="Tell the school why — they see this word for word."
                        value={otherText}
                        onChange={(e) => setOtherText(e.target.value)}
                        sx={{ mt: 1 }}
                    />
                )}

                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 1.25, mt: 3 }}>
                    {!canDecline && (
                        <Typography sx={{ fontSize: '12.5px', color: MUTED, mr: 'auto' }}>
                            A reason is required.
                        </Typography>
                    )}
                    <Button variant="outlined" sx={secondaryButtonSx} onClick={onKeepRequest}>
                        Keep request
                    </Button>
                    <Button sx={primaryButtonSx} disabled={!canDecline}
                            onClick={() => onConfirmDecline(finalReason)}>
                        Decline
                    </Button>
                </Box>
            </Box>
        </Dialog>
    );
};

/**
 * What each primary action does, for its tooltip. The button label alone
 * ("Reopen", "Update Progress") does not say what pressing it will do.
 */
const ACTION_HINTS = {
    'Review Request':  'Open the request, adjust the scope, then accept or decline.',
    'Suggest Program': 'Build a program suggestion for this school.',
    'Update Progress': 'Open the build you started for this request.',
    'Reopen':          'Move this suggestion back to Ready to Suggest so you can send it again.',
    'Link Program':    'Open My Schools to link this program to the school.',
    'View School':     'Open the school record.',
    'Contact School':  'Email the school contact.',
    'Approve unlock':  'Approve the eligible unlock request and continue the entitlement process.',
    'Approve licences': 'Assign the requested learner licences to the school now.',
};

const OpportunityRow = ({ row, onActionClick, onMenuAction }) => (
    <Box sx={{
        ...plainCardSx, p: { xs: 2, sm: 2.5 },
        display: 'flex', flexDirection: { xs: 'column', sm: 'row' },
        alignItems: { xs: 'stretch', sm: 'center' }, justifyContent: 'space-between', gap: 2,
    }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, flex: 1, minWidth: 0 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
                <Box>
                    <Typography sx={{ fontSize: '12px', color: MUTED }}>{row.date}</Typography>
                    {row.dateNote && (
                        <Typography sx={{ fontSize: '11px', color: MUTED }}>{row.dateNote}</Typography>
                    )}
                </Box>
                <TypeChip type={row.type} />
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, flexWrap: 'wrap' }}>
                <Typography sx={{ fontSize: '15px', fontWeight: 800, color: TEXT }}>
                    {row.org}
                </Typography>
                <Typography sx={{ fontSize: '13px', color: MUTED }}>
                    {row.context}
                </Typography>
            </Box>
            <Box>
                <Typography sx={{ fontSize: '14px', fontWeight: 700, color: TEXT, mb: row.desc ? 0.25 : 0 }}>
                    {row.title}
                </Typography>
                {row.desc && (
                    <Typography sx={{ fontSize: '13px', color: MUTED, lineHeight: 1.6 }}>
                        {row.desc}
                    </Typography>
                )}
            </Box>
        </Box>

        <Box sx={{
            display: 'flex', flexDirection: { xs: 'row', sm: 'column' },
            alignItems: { xs: 'center', sm: 'flex-end' }, justifyContent: { xs: 'space-between', sm: 'center' },
            gap: 0.25, minWidth: { sm: '110px' }, textAlign: { sm: 'right' },
        }}>
            <Typography sx={{ fontSize: '14px', fontWeight: 800, color: TEXT }}>{row.qty}</Typography>
            <Typography sx={{ fontSize: '12px', color: MUTED }}>{row.qtyContext}</Typography>
        </Box>

        <Box sx={{
            display: 'flex', flexDirection: 'row', flexWrap: 'wrap',
            alignItems: 'center', justifyContent: 'flex-end', gap: 1.25,
        }}>
            {/*
              * A declined row carries the reason the school was given. It is the
              * one piece of context that explains the row and it was not shown
              * anywhere, so it sits on the chip the reader is already looking at.
              */}
            <Tooltip
                arrow
                placement="top"
                title={row.status === 'declined' && row.declineReason
                    ? `Reason given: ${row.declineReason}`
                    : (row.statusHint || STATUS_HINTS[row.status] || '')}
            >
                <span><StatusChip status={row.status} /></span>
            </Tooltip>
            {/* The school asked for changes to a request already accepted: what it
                asked for opens here, as the changes-requested mail says. */}
            {row.changeRequested && (
                <Button sx={secondaryButtonSx} onClick={() => onMenuAction && onMenuAction('Review Changes', row)}>
                    Review Changes
                </Button>
            )}
            {/* The school asked for changes to a finished program: Publish stays, and
                this opens the program so the partner can make them first. */}
            {row.changeRequested && row.status === 'ready_to_publish' && (
                <Tooltip arrow placement="top" title="Open the program to make the changes the school asked for.">
                    <span>
                        <Button sx={secondaryButtonSx} onClick={() => onMenuAction && onMenuAction('Update Progress', row)}>
                            Update Progress
                        </Button>
                    </span>
                </Tooltip>
            )}
            {row.action && (
                <Tooltip arrow placement="top" title={ACTION_HINTS[row.action] || ''}>
                    <span>
                        <Button
                            sx={primaryButtonSx}
                            {...(row.action === 'Contact School' && contactSchoolEmail(row)
                                ? contactSchoolLink(row)
                                : { onClick: () => onActionClick && onActionClick(row) })}
                        >
                            {row.action}
                        </Button>
                    </span>
                </Tooltip>
            )}
            <RowKebabMenu row={row} onMenuAction={onMenuAction} />
        </Box>
    </Box>
);

const OpportunityGroup = ({ group, onActionClick, onMenuAction }) => (
    <Box sx={{ mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
            <Typography sx={{ fontSize: '15px', fontWeight: 800, color: TEXT }}>
                {group.title}
            </Typography>
            <Box sx={{
                display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                minWidth: 20, height: 20, px: 0.5, borderRadius: '50px',
                backgroundColor: 'rgba(255, 105, 0, 0.1)',
            }}>
                <Typography sx={{ fontSize: '11.5px', fontWeight: 700, color: ORANGE }}>
                    {group.count}
                </Typography>
            </Box>
        </Box>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            {group.rows.map((row) => (
                <OpportunityRow key={row.id} row={row}
                                onActionClick={onActionClick} onMenuAction={onMenuAction} />
            ))}
        </Box>
        {group.footerNote && (
            <Typography sx={{ fontSize: '12.5px', color: MUTED, lineHeight: 1.6, mt: 2, pt: 2, borderTop: `1px solid ${BORDER}` }}>
                {group.footerNote}
            </Typography>
        )}
    </Box>
);

const filterSelectSx = {
    fontSize: '13.5px', color: TEXT, backgroundColor: 'var(--brand-surface, #ffffff)',
    borderRadius: '50px', minWidth: '110px',
    '& .MuiOutlinedInput-notchedOutline': { borderColor: BORDER },
    '& .MuiSelect-select': { display: 'flex', alignItems: 'center', py: 0.9, px: 2 },
};

const OpportunityPipelinePage = () => {
    useGeo(); // re-render with the API's country names once they load
    const { authUser } = useAuth();
    const history = useHistory();
    /*
     * The mockup's "P1" tag covers P1A and P1B — but neither is a PLAN.
     *
     * DQ_PRO_PLAN_KEY.ENTERPRISE_P1A and _P1B exist as constants, yet no account
     * carries them: the real keys are enterprise_p0, enterprise_p1, enterprise_p2…
     * and P1A/P1B are the two shapes of enterprise_p1, told apart by whether
     * LearnCreator has been assigned. Gating on the package constants therefore
     * matched nobody, and Review Request never opened its dialog for any user.
     *
     * enterprise_p1 is the plan that carries both, so that is the gate.
     */
    const isP1Family = authUser?.dq_pro_plan?.plan_key === DQ_PRO_PLAN_KEY.ENTERPRISE_P1;

    const [activeTab, setActiveTab] = useState('open');
    const [activeType, setActiveType] = useState('all');
    const [search, setSearch] = useState('');
    const [status, setStatus] = useState('all');
    const [country, setCountry] = useState('all');
    const [solution, setSolution] = useState('all');
    const [suggestDialogOpen, setSuggestDialogOpen] = useState(false);
    // The draft the dialog is about. Without it the send had nothing to
    // address, which is why it only ever swapped dialogs.
    const [suggestRow, setSuggestRow] = useState(null);
    const [sendingSuggestion, setSendingSuggestion] = useState(false);
    const [suggestionSentOpen, setSuggestionSentOpen] = useState(false);
    // Slide 20's basket: this school's Ready to Suggest drafts, and which are ticked.
    const [schoolDrafts, setSchoolDrafts] = useState([]);
    const [checkedDraftIds, setCheckedDraftIds] = useState(() => new Set());
    const [loadingDrafts] = useState(false);
    const [sentCount, setSentCount] = useState(0);
    // edit cohort (slide 20): the school's grade / class options, and each row's pick.
    // The school's grades and classes, in TargetGroupFields' option shape.
    const [cohortChoices, setCohortChoices] = useState({ grades: [], classes: [] });
    // The suggestion whose cohort is being edited.
    const [cohortEditRow, setCohortEditRow] = useState(null);
    const [cohortById, setCohortById] = useState(() => new Map());
    // The session count the partner sets per suggestion before sending (PG-01 prints it).
    const [planById, setPlanById] = useState(() => new Map());
    // PG-05 — the suggestion whose changes are being reviewed.
    const [changesRow, setChangesRow] = useState(null);
    // Read-only: a change request on a request the partner already accepted.
    const [changesViewRow, setChangesViewRow] = useState(null);
    const [sendingBack, setSendingBack] = useState(false);
    const [reviewRow, setReviewRow] = useState(null);
    // Which row is mid-publish, so its button can say so and not re-fire.
    const [publishingId, setPublishingId] = useState(null);
    // { row, stage: 'confirm' | 'published' } — the shared publish dialog.
    const [publishDialog, setPublishDialog] = useState(null);
    const [reviewDialogOpen, setReviewDialogOpen] = useState(false);
    const [declineDialogOpen, setDeclineDialogOpen] = useState(false);
    // The unlock request being approved, and whether the approval is in flight.
    const [approveRow, setApproveRow] = useState(null);
    const [approving, setApproving] = useState(false);
    // The licence request being approved — its own dialog, same in-flight flag.
    const [approveLicenceRow, setApproveLicenceRow] = useState(null);

    /**
     * The kebab menu — BTN-1139 to BTN-1142.
     *
     * "Contact school" has no in-app destination in the registry ("External —
     * contact school/network"), so it opens the reader's mail client against
     * the address the request was raised from. Without one, say so rather than
     * opening an empty compose window.
     */
    /*
     * BTN-1140 points at PW-ALL-My Schools-Main. There is no per-school detail
     * route — /dq_pro/clients/list is the only one — so the school code rides
     * along as a query the list can preselect on. Landing on the list is right
     * either way; a deep link would 404.
     *
     * Shared with the ACTION column: a Completed row's button is "View School"
     * and the kebab's item is "View school". They are the same destination, so
     * they must not be two implementations that can drift apart — which is
     * exactly how the button ended up doing nothing at all.
     */
    const goToSchool = (row) => {
        const code = row.raw?.org_code;
        history.push(code ? `/dq_pro/clients/list?org_code=${encodeURIComponent(code)}` : '/dq_pro/clients/list');
    };

    /* Same pairing: "Contact School" in the ACTION column, "Contact school" in
       the kebab. The registry marks both External, so mail is the destination.
       With an address both render as the mailto link itself (contactSchoolLink),
       so this is only reached when the school has none. */
    const contactSchool = () => {
        toast(TOAST_TYPES.ERROR, 'No contact address', 'This school has no contact email on record.');
    };

    const handleMenuAction = (item, row) => {
        // From a Ready to Publish row whose school asked for changes.
        if (item === 'Update Progress') { if (row?.raw) resumeBuild(row.raw); return; }
        if (item === 'Review Changes') { setChangesViewRow(row); return; }
        if (item === 'View school')    { goToSchool(row);   return; }
        if (item === 'Contact school') { contactSchool(row); return; }
        if (item === 'Decline') {
            setReviewRow(row);
            setDeclineDialogOpen(true);
        }
        if (item === 'Approve unlock') {
            if (!row.canApprove) {
                toast(TOAST_TYPES.ERROR, 'Cannot approve yet', row.approveBlockedReason || 'This request cannot be approved right now.');
                return;
            }
            setApproveRow(row);
        }
        if (item === 'Accept request') acceptUnlock(row);
        if (item === 'Approve licences') setApproveLicenceRow(row);
    };

    /** Assign the licences (CP-01/LIC goes to the requester). */
    const handleApproveLicences = async (licences) => {
        const row = approveLicenceRow;
        if (!row) return;
        setApproving(true);
        try {
            await decideLicenceRequestService({ request_id: row.raw?._id, decision: 'approve', licences });
            toast(TOAST_TYPES.SUCCESS, 'Licences Assigned',
                `${licences} learner licence${licences === 1 ? '' : 's'} assigned to ${row.org}, and they have been notified.`);
            setApproveLicenceRow(null);
            loadRows();
        } catch (e) {
            /* invokeApi toasts the reason; the request stays open */
        } finally {
            setApproving(false);
        }
    };

    /** Take an unlock request on without granting it — it moves to In Progress. */
    const acceptUnlock = async (row) => {
        try {
            await decideUnlockRequestService({ request_id: row.raw?._id, decision: 'accept' });
            toast(TOAST_TYPES.SUCCESS, 'Request Accepted', `${row.org}'s request is in progress. Approve it once you can grant it.`);
            loadRows();
        } catch (e) {
            /* invokeApi toasts the reason */
        }
    };

    /** Grant the capability (RQ-03 goes to the requester). */
    const handleApproveUnlock = async (variant) => {
        const row = approveRow;
        if (!row) return;
        setApproving(true);
        try {
            await decideUnlockRequestService({ request_id: row.raw?._id, decision: 'approve', variant });
            toast(TOAST_TYPES.SUCCESS, 'Request Approved', `${row.org} has been given access and notified.`);
            setApproveRow(null);
            loadRows();
        } catch (e) {
            /* invokeApi toasts the reason */
        } finally {
            setApproving(false);
        }
    };

    /**
     * Publish the program built for this request.
     *
     * Two steps, in this order: publish the PROGRAM, then mark the REQUEST
     * delivered. Reversing them would tell the school a program is ready
     * while it is still a draft they cannot open.
     *
     * The API refuses to publish content ML has not finished, so a stale
     * page that still shows the button gets a clear error rather than a
     * half-delivered request.
     */
    const publishForRow = async () => {
        const row = publishDialog?.row;
        const r = row?.raw;
        if (!r?.program_code || publishingId) return;
        setPublishingId(r._id);
        try {
            const res = await publishLearningProgramService(r.program_code);
            if (res?.error) {
                // The API refuses content ML has not finished. Close the dialog
                // and say why, rather than leaving it stuck on the confirm step.
                setPublishDialog(null);
                toast(TOAST_TYPES.ERROR, 'Could not publish', res.error_message || 'Please try again.');
                return;
            }
            await updateProgramRequestStatusService({
                request_id:   r._id,
                status:       WORKSPACE_REQUEST_STATUS.BUILT,
                program_code: r.program_code,
            });
            // Success step of the same dialog, not a toast-and-vanish.
            setPublishDialog({ row, stage: 'published' });
            loadRows();
        } catch (e) {
            setPublishDialog(null);
            toast(TOAST_TYPES.ERROR, 'Could not publish', e?.message || 'Please try again.');
        } finally {
            setPublishingId(null);
        }
    };

    const handleActionClick = (row) => {
        // Completed rows offer "View School"; anything with no action of its own
        // falls back to "Contact School". Neither had a branch here, so both
        // rendered as live buttons that did nothing when clicked.
        if (row.action === 'View School')    { goToSchool(row);   return; }
        // Ready to Link → My Schools (the list itself, not the linking pop-up).
        if (row.action === 'Link Program')   { goToSchool(row);   return; }
        if (row.action === 'Contact School') { contactSchool(row); return; }
        // An unlock or licence request not approved yet: the button is the row
        // menu's own item, so it does exactly what the menu does.
        if (row.action === 'Approve unlock' || row.action === 'Approve licences') {
            handleMenuAction(row.action, row);
            return;
        }
        if (row.action === 'Suggest Program') {
            openSuggestBasket(row);
            return;
        }
        if (row.action === 'Review Changes') {
            setChangesRow(row);
            loadCohortChoices(row);
            return;
        }
        if (row.action === 'Reopen') {
            reopenSuggestion(row);
            return;
        }
        // Update Progress — back into the build already under way. Same
        // handoff as Accept, so the creator restores this request's chat.
        if (row.action === 'Update Progress') {
            if (row.raw) resumeBuild(row.raw);
            return;
        }
        if (row.action === 'Publish') {
            // Confirm first — publishing is one-way and emails the school.
            if (row.raw?.program_code) setPublishDialog({ row, stage: 'confirm' });
            return;
        }
        // Review Request → build-confirm dialog, gated to enterprise_p1 and
        // only for a program-build request (not a Credit Request row).
        if (row.action === 'Review Request' && row.type === 'program' && isP1Family) {
            setReviewRow(row);
            setReviewDialogOpen(true);
        }
    };

    /**
     * Send the drafted suggestion to the school.
     *
     * status 10 -> 0. The API refuses this for anything that is not a
     * datahouse draft, so a stale row cannot be re-opened from here.
     *
     * The confirmation dialog (slide 21) opens only after the write lands —
     * telling the partner their suggestion was shared when it was not is the
     * one outcome worth guarding against here.
     */
    /**
     * Open slide 20 for one school.
     *
     * Opens at once with the row that was clicked (ticked), then fills in the
     * school's other Ready to Suggest drafts from the API — org_code + tab, both
     * already supported by the list endpoint. Those start unticked. If the fetch
     * fails, the dialog simply keeps the one row it opened with.
     */
    const openSuggestBasket = async (row) => {
        setSuggestRow(row);
        setSchoolDrafts([row]);
        setCheckedDraftIds(new Set([row.id]));
        setCohortById(new Map());
        setPlanById(new Map());
        setCohortChoices({ grades: [], classes: [] });
        setSuggestDialogOpen(true);

        loadCohortChoices(row);
        // One row only — the suggestion that was clicked. The school's other
        // drafts are already in the table behind the dialog.
    };

    /*
     * The school's own grades and classes, for "edit cohort" (slide 20) and for
     * Review Changes. Independent of the dialog's other data: a failure leaves
     * only Whole school to choose.
     */
    const loadCohortChoices = (row) => {
        setCohortChoices({ grades: [], classes: [] });
        const org_code = row?.raw?.org_code;
        if (!org_code) return;
        listOrgClassesService(org_code)
            .then((sections) => {
                const rows = Array.isArray(sections) ? sections : [];
                // Only this suggestion's school: in a network, classes are per school.
                const inst = row?.raw?.institute_code || '';
                const own = rows.filter((c) => !inst || !c.institute_code || c.institute_code === inst);
                const grades = [...new Set(own.map((c) => String(c.section_level || '').trim()).filter(Boolean))].sort()
                    .map((g) => ({ value: g, label: g, institute_code: '' }));
                const classes = own
                    .filter((c) => c.section_name)
                    .map((c) => ({ value: c.section_name, label: c.section_name, institute_code: c.institute_code || '' }));
                setCohortChoices({ grades, classes });
            })
            .catch(() => setCohortChoices({ grades: [], classes: [] }));
    };

    const setDraftPlan = (id, plan) => {
        setPlanById((prev) => {
            const next = new Map(prev);
            next.set(id, plan);
            return next;
        });
    };

    const setDraftCohort = (id, option) => {
        setCohortById((prev) => {
            const next = new Map(prev);
            next.set(id, option);
            return next;
        });
    };

    /**
     * Send the reviewed suggestion back to the school (PG-05 → PG-01).
     */
    const handleSendBack = async ({ num_sessions, grade_level, class_name }) => {
        const id = changesRow?.id || changesRow?.raw?._id;
        if (!id || sendingBack) return;
        setSendingBack(true);
        try {
            const res = await updateProgramRequestStatusService({
                request_id: id,
                status: WORKSPACE_REQUEST_STATUS.PENDING,
                num_sessions,
                // The cohort the partner settled — '' on both is whole school.
                grade_level,
                class_name,
            });
            if (res?.error) {
                toast(TOAST_TYPES.ERROR, 'Could not send it back', res.error_message || 'Please try again.');
                return;
            }
            toast(TOAST_TYPES.SUCCESS, 'Sent back', `${changesRow?.org || 'The school'} can review the revised suggestion.`);
            setChangesRow(null);
            loadRows();
        } catch (e) {
            toast(TOAST_TYPES.ERROR, 'Could not send it back', e?.message || 'Please try again.');
        } finally {
            setSendingBack(false);
        }
    };

    /** PW slide 19 — a declined suggestion back to Ready to Suggest. No email. */
    const reopenSuggestion = async (row) => {
        const id = row?.id || row?.raw?._id;
        if (!id) return;
        try {
            const res = await updateProgramRequestStatusService({
                request_id: id,
                status: WORKSPACE_REQUEST_STATUS.READY_TO_SUGGEST,
            });
            if (res?.error) {
                toast(TOAST_TYPES.ERROR, 'Could not reopen', res.error_message || 'Please try again.');
                return;
            }
            toast(TOAST_TYPES.SUCCESS, 'Reopened', `${row.title || 'The suggestion'} is back in Ready to Suggest.`);
            loadRows();
        } catch (e) {
            toast(TOAST_TYPES.ERROR, 'Could not reopen', e?.message || 'Please try again.');
        }
    };

    const toggleDraft = (id) => {
        setCheckedDraftIds((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id); else next.add(id);
            return next;
        });
    };

    /**
     * Send every ticked draft, one request each (10 → 0), in order.
     *
     * One at a time rather than in parallel, so a failure part-way is easy to
     * report and nothing races the per-row guard on the server. The school gets
     * one email per suggestion sent — that is the existing per-request mail.
     */
    const handleSendSuggestion = async () => {
        const ids = schoolDrafts.map((d) => d.id).filter((id) => checkedDraftIds.has(id));
        if (!ids.length || sendingSuggestion) return;
        setSendingSuggestion(true);
        let sent = 0;
        let firstError = '';
        try {
            for (const id of ids) {
                try {
                    // The cohort picked with "edit cohort", when one was picked.
                    const cohort = cohortById.get(id);
                    /*
                     * The session count travels with every send, so PG-01 always
                     * has its Sessions line: the partner's choice, else what the
                     * row carries, else slide 20's 5 — the figure the dialog
                     * showed and costed. No length (Ian, 17 Sep).
                     */
                    const draftRow = schoolDrafts.find((d) => d.id === id);
                    const plan = planById.get(id) || {};
                    const num_sessions = Number(plan.sessions) || Number(draftRow?.raw?.num_sessions) || 5;
                    const res = await updateProgramRequestStatusService({
                        request_id: id,
                        status:     WORKSPACE_REQUEST_STATUS.PENDING,
                        num_sessions,
                        ...(cohort ? { grade_level: cohort.grade_level, class_name: cohort.class_name } : {}),
                    });
                    if (res?.error) { firstError = firstError || res.error_message || ''; continue; }
                    sent += 1;
                } catch (e) {
                    firstError = firstError || e?.message || '';
                }
            }
            if (sent < ids.length) {
                toast(
                    TOAST_TYPES.ERROR,
                    sent ? `Sent ${sent} of ${ids.length} suggestions` : 'Could not send suggestion',
                    firstError || 'Please try again.',
                );
            }
            if (sent > 0) {
                setSentCount(sent);
                setSuggestDialogOpen(false);
                setSuggestionSentOpen(true);
            }
            // Sent rows leave Ready to Suggest and appear under In Progress as "Suggested".
            loadRows();
        } finally {
            setSendingSuggestion(false);
        }
    };

    /**
     * Accept — the partner takes the request on and goes straight into the build.
     *
     * Same handoff the request inbox uses (ProgramRequests.page.jsx): stash the
     * request in sessionStorage under PENDING_REQUEST_KEY, mark it IN_PROGRESS so
     * the row stops offering Review Request while a build is underway, then push
     * into the creator pre-filled. Publishing there is what marks the request
     * delivered and emails the school — so accepting and delivering stay one
     * continuous flow rather than two disconnected buttons.
     */
    /**
     * Open the creator on a request, resuming the program already started for
     * it.
     *
     * Shared by Accept (first time in) and Update Progress (coming back). The
     * stash tells the creator which request it is building and which school to
     * link to on publish; programCode is what makes it restore THIS request's
     * chat instead of whatever was last open in the workspace, so two requests
     * never end up sharing one conversation.
     */
    const resumeBuild = (r) => {
        try {
            sessionStorage.setItem(PENDING_REQUEST_KEY, JSON.stringify({
                id: r._id,
                school_name: r.school_name || r.org_code || '',
                org_code: r.org_code || '',
                program_code: r.program_code || '',
            }));
        } catch { /* sessionStorage unavailable — the build still works */ }

        // WIZARD field names (DEFAULT_WIZARD_VALUES), matching the request
        // inbox exactly, so the creator pre-fills from either entry point.
        /*
         * A request with no program yet opens the main LearnCreator page, not
         * the chat (LA-PW-13Oct-02); that page hands the request on to the
         * builder. One already being built reopens ITS program, as before.
         */
        history.push(r.program_code ? '/dq_pro/program/creation/preview' : LEARNCREATOR_START_PATH, {
            programRequest: {
                request_id:                 r._id,
                programCode:                r.program_code || '',
                Theme:                      r.topic || '',
                NumSessions:                r.num_sessions || null,
                // A suggestion carries no session length (Ian, 17 Sep).
                SessionDuration:            String(r.source) === 'suggested' ? null : (r.session_duration_mins || null),
                SubjectContextTopic:        r.school_subject || '',
                CulturalSensitivityDetails: r.local_sensitivities || '',
                grade_level:                r.grade_level || '',
                AdditionalInfo:             r.additional_info || '',
            },
        });
    };

    /**
     * Accept the request and open the creator on it.
     *
     * `adjusted` is what the owner left in the dialog's dropdowns. Slide 22
     * offers them under "Adjust anything below", so they are the agreed scope,
     * not the original ask — and three places have to agree on it: the request
     * row, the PG-03 email the school receives, and the build prompt. So it is
     * sent to the API first and only then used locally; the server echoes it
     * into the email, and resumeBuild carries the same numbers into the wizard.
     */
    const handleAcceptRequest = async (adjusted = {}) => {
        const r = reviewRow?.raw;
        setReviewDialogOpen(false);
        if (!r) { setReviewRow(null); return; }

        const num_sessions = Number(adjusted.sessions) || Number(r.num_sessions) || null;
        // Not chosen in the dialog any more: whatever the request already holds.
        const session_duration_mins = Number(r.session_duration_mins) || null;

        if (Number(r.status) === WORKSPACE_REQUEST_STATUS.PENDING) {
            await updateProgramRequestStatusService({
                request_id: r._id,
                status: WORKSPACE_REQUEST_STATUS.IN_PROGRESS,
                num_sessions,
                session_duration_mins,
            }).catch(() => {});
        }

        toast(TOAST_TYPES.SUCCESS, 'Request Accepted', `Building ${r.topic || 'the program'} for ${r.school_name || 'the school'}.`);
        setReviewRow(null);
        // Build from the AGREED scope, not the row as it was fetched.
        resumeBuild({ ...r, num_sessions, session_duration_mins });
    };

    /** Decline — reason required, then PG-06 to the school. */
    const handleConfirmDecline = async (reason) => {
        const r = reviewRow?.raw;
        setDeclineDialogOpen(false);
        if (!r || !reason) { setReviewRow(null); return; }
        try {
            if (reviewRow?.type === 'unlock') {
                // RQ-04 to the requester, with the reason quoted.
                await decideUnlockRequestService({ request_id: r._id, decision: 'decline', decline_reason: reason });
            } else if (reviewRow?.type === 'license') {
                // CP-04/LIC to the requester, with the reason quoted.
                await decideLicenceRequestService({ request_id: r._id, decision: 'decline', decline_reason: reason });
            } else {
                await updateProgramRequestStatusService({
                    request_id: r._id,
                    status: WORKSPACE_REQUEST_STATUS.DECLINED,
                    decline_reason: reason,
                });
            }
            toast(TOAST_TYPES.SUCCESS, 'Request Declined', `${r.school_name || 'The school'} has been notified.`);
            loadRows();
        } catch (e) {
            toast(TOAST_TYPES.ERROR, 'Could not decline', e?.message || 'Please try again.');
        }
        setReviewRow(null);
    };


    const handleDeclineFromReview = () => {
        setReviewDialogOpen(false);
        setDeclineDialogOpen(true);
    };

    const handleKeepRequest = () => {
        setDeclineDialogOpen(false);
        // An unlock or licence request has no review dialog to go back to.
        if (reviewRow?.type === 'unlock' || reviewRow?.type === 'license') { setReviewRow(null); return; }
        setReviewDialogOpen(true);
    };


    /*
     * ── Live data ─────────────────────────────────────────────────────────────
     *
     * Reads the same endpoint the partner's request inbox uses, with
     * request_type=all so the unlock requests schools and networks raise from
     * "Unlock a capability" list beside program requests, and so the licence
     * requests a short class publish raises list too. Credit rows have no
     * backend yet, so that type filter returns nothing rather than showing
     * invented rows.
     */
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(false);
    const [creditBalance, setCreditBalance] = useState(null);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [total, setTotal] = useState(0);
    const [statusCounts, setStatusCounts] = useState({});
    // Each tab's label, counted by the API with the same rule that lists its rows.
    const [apiTabCounts, setApiTabCounts] = useState(null);
    // Sessions across every open request in the workspace — see loadRows.
    const [openSessions, setOpenSessions] = useState(0);
    const [countryOptions, setCountryOptions] = useState([]);

    /**
     * An unlock request → a pipeline row, per PW-ALL-Growth-Pipeline's
     * "Upgrade Request" row: the product requested ("LearnCreator"), the
     * country, "Requested by School Coordinator", "160 learners", Awaiting
     * Response, Contact School, and Approve unlock in the menu.
     *
     * No plan codes: the row names what was asked for, not a "P0 → P1" move
     * (product owner, 12 Oct 2026).
     */
    const toUnlockRow = (r) => {
        const s = Number(r.status);
        const statusKey = s === WORKSPACE_REQUEST_STATUS.DECLINED ? 'declined'
            : s === WORKSPACE_REQUEST_STATUS.BUILT ? 'completed'
            // Accepted: the partner took it on and approves once it can.
            : s === WORKSPACE_REQUEST_STATUS.ACCEPTED ? 'under_review'
            : 'awaiting_response';
        const tab = statusKey === 'under_review' ? 'in_progress'
            : statusKey === 'awaiting_response' ? 'open'
            : statusKey;

        const granted = r.granted_capability_label && r.granted_capability_label !== r.requested_capability_label
            ? ` · Granted as ${r.granted_capability_label.replace(/^.*— /, '')}`
            : '';

        return {
            id: r._id,
            date: r.createdAt
                ? new Date(r.createdAt).toLocaleDateString(undefined, { day: '2-digit', month: 'short', year: 'numeric' })
                : '—',
            type: 'unlock',
            org: r.school_name || r.org_code || '—',
            context: countryName(r.org_country) || '',
            title: r.requested_capability_label || 'Capability request',
            desc: `Requested by ${r.requested_by_label || 'School Coordinator'}${granted}`,
            qty: r.learner_count ? `${r.learner_count} learners` : '—',
            qtyContext: '',
            status: statusKey,
            statusHint: statusKey === 'awaiting_response'
                ? `Waiting on you to review this request for ${r.requested_capability_label || 'a capability'}.`
                : statusKey === 'under_review'
                ? `You accepted this request. Approve it once you can grant ${r.requested_capability_label || 'it'}.`
                    + (r.approve_blocked_reason ? ` ${r.approve_blocked_reason}` : '')
                : statusKey === 'completed' && r.granted_to === 'network'
                ? `Approved for ${r.approve_target_name || 'their network'} — the network decides whether ${r.school_name || 'this school'} gets ${r.granted_capability_label || r.requested_capability_label || 'it'}.`
                : statusKey === 'completed'
                ? `Approved — ${r.granted_capability_label || r.requested_capability_label || 'the capability'} is active for them.`
                : '',
            changeRequested: false,
            // Not approved yet → Approve unlock (LA-PW-22Oct-01: the button names what
            // it approves); Contact school stays in the row menu.
            action: statusKey === 'completed' ? 'View School' : statusKey === 'declined' ? 'Contact School' : 'Approve unlock',
            tab,
            inbound: true,
            declineReason: r.decline_reason || '',
            canApprove: !!r.can_approve,
            approveBlockedReason: r.approve_blocked_reason || '',
            approveVariants: r.approve_variants || [],
            // A school inside a network: approval reaches its network instead.
            approveToNetwork: r.approve_target === 'network',
            approveTargetName: r.approve_target_name || '',
            raw: r,
        };
    };

    /**
     * A licence request → a pipeline row: the school, how many licences, how
     * many learners are waiting, and Approve licences in the menu. Approved
     * reads Completed; there is no in-between state.
     */
    const toLicenceRow = (r) => {
        const s = Number(r.status);
        const statusKey = s === WORKSPACE_REQUEST_STATUS.DECLINED ? 'declined'
            : s === WORKSPACE_REQUEST_STATUS.BUILT ? 'completed'
            : 'awaiting_response';
        const tab = statusKey === 'awaiting_response' ? 'open' : statusKey;
        const asked = Number(r.requested_licences) || 0;
        const granted = Number(r.granted_licences) || 0;
        const licences = (n) => `${n} learner licence${n === 1 ? '' : 's'}`;
        const freeNow = r.licences_available_now !== null && r.licences_available_now !== undefined
            && statusKey === 'awaiting_response'
            ? ` · ${r.licences_available_now} available now` : '';

        return {
            id: r._id,
            date: r.createdAt
                ? new Date(r.createdAt).toLocaleDateString(undefined, { day: '2-digit', month: 'short', year: 'numeric' })
                : '—',
            type: 'license',
            org: r.school_name || r.org_code || '—',
            context: countryName(r.org_country) || '',
            title: licences(asked),
            desc: `Requested by ${r.requested_by_label || 'School Coordinator'}${freeNow}`,
            qty: r.learner_count ? `${r.learner_count} learners` : '—',
            qtyContext: '',
            status: statusKey,
            statusHint: statusKey === 'awaiting_response'
                ? `${r.school_name || 'This school'} cannot publish its classes until it has ${licences(asked)} more.`
                : statusKey === 'completed'
                ? `Approved — ${licences(granted || asked)} assigned.`
                : '',
            changeRequested: false,
            // Not approved yet → Approve licences (LA-PW-22Oct-01: the button names what
            // it approves); Contact school stays in the row menu.
            action: statusKey === 'completed' ? 'View School' : statusKey === 'declined' ? 'Contact School' : 'Approve licences',
            tab,
            inbound: true,
            declineReason: r.decline_reason || '',
            canApprove: !!r.can_approve,
            raw: r,
        };
    };

    /** WorkspaceRequest → a pipeline row. */
    const toRow = (r) => {
        if (r.request_type === 'unlock') return toUnlockRow(r);
        if (r.request_type === 'licences') return toLicenceRow(r);
        const s = Number(r.status);
        /*
         * 10 is the datahouse draft (Schema · workspacerequests). It is checked
         * FIRST because it is the only status that is not part of a conversation
         * with the school — nothing has been sent yet.
         */
        const statusKey = s === WORKSPACE_REQUEST_STATUS.READY_TO_SUGGEST ? 'ready_to_suggest'
            : s === WORKSPACE_REQUEST_STATUS.DECLINED ? 'declined'
            /*
             * Published is not yet Completed: the program still has to be linked
             * to the school. program_linked comes from the API (the school's org
             * has the program). Only an explicit false holds the row back, so a
             * failed lookup never demotes a finished request.
             */
            : s === WORKSPACE_REQUEST_STATUS.BUILT ? (r.program_linked === false ? 'ready_to_link' : 'completed')
            /*
             * IN_PROGRESS splits on what ML reports. program_is_ready comes
             * from _attachBuildState on the API and is the same field the
             * request inbox publishes from — one source, so the two screens
             * cannot disagree about whether a build is done.
             */
            : s === WORKSPACE_REQUEST_STATUS.IN_PROGRESS
                ? (r.program_is_ready ? 'ready_to_publish' : 'building_program')
            : s === WORKSPACE_REQUEST_STATUS.ACCEPTED ? 'building_program'
            : s === WORKSPACE_REQUEST_STATUS.CHANGES_REQUESTED ? 'under_review'
            : String(r.source) === 'suggested' ? 'suggested' : 'awaiting_response';

        // Which tab a row belongs to, from its status alone.
        const tab = statusKey === 'ready_to_suggest' ? 'ready_to_suggest'
            : statusKey === 'declined' ? 'declined'
            // Ready to Link is listed by the API under Completed (a published request).
            : (statusKey === 'completed' || statusKey === 'ready_to_link') ? 'completed'
            : ['building_program', 'ready_to_publish', 'suggested', 'under_review'].includes(statusKey) ? 'in_progress'
            : 'open';

        const changeRequested = (s === WORKSPACE_REQUEST_STATUS.IN_PROGRESS || s === WORKSPACE_REQUEST_STATUS.ACCEPTED)
            && !!String(r.requested_changes || '').trim();

        // Inbound = the school asked. Outbound = the partner suggested. Slide 17
        // splits In Progress by exactly this.
        const inbound = String(r.source) !== 'suggested';

        return {
            id: r._id,
            date: r.createdAt
                ? new Date(r.createdAt).toLocaleDateString(undefined, { day: '2-digit', month: 'short', year: 'numeric' })
                : '—',
            type: 'program',
            org: r.school_name || r.org_code || '—',
            context: [countryName(r.org_country), (r.associated_dq_codes || []).join(', ')].filter(Boolean).join(' · '),
            title: r.topic || '—',
            // The widest-capability-gap sentence the datahouse wrote, which is
            // the whole reason the suggestion exists. Slide 22 prints it under
            // the topic as the evidence line.
            /*
             * A school that asked for changes while the partner works on it
             * (Program Ready → Request Changes, Ian / Usman 2 Oct 2026): its
             * message shows here, on the row the partner edits and publishes from.
             */
            desc: (s === WORKSPACE_REQUEST_STATUS.IN_PROGRESS || s === WORKSPACE_REQUEST_STATUS.ACCEPTED) && r.requested_changes
                ? `Changes requested: ${r.requested_changes}`
                : (r.gap_label || r.local_sensitivities || ''),
            /*
             * A draft has no sessions yet — the datahouse sizes it by how many
             * learners the gap covers, which is what slide 22 shows in this
             * column ("320 LICENSES"). A school-raised row keeps the sessions
             * the school asked for.
             */
            qty: statusKey === 'ready_to_suggest' && r.quantity
                ? `${r.quantity} ${r.quantity_unit || 'learners'}`
                // A sent suggestion: its session count, never a length (Ian, 17 Sep).
                : !inbound && r.num_sessions
                ? `${r.num_sessions} session${Number(r.num_sessions) === 1 ? '' : 's'}`
                : r.num_sessions && r.session_duration_mins
                ? `${r.num_sessions} × ${r.session_duration_mins} min`
                // Suggestions carry a count and no length.
                : r.num_sessions
                ? `${r.num_sessions} session${Number(r.num_sessions) === 1 ? '' : 's'}`
                : '—',
            qtyContext: groupTextOf(r),
            status: statusKey,
            // The school asked for changes while the partner works on it: the note
            // shows on the row, and a finished program also offers Update Progress
            // beside Publish. Publishing settles the request.
            changeRequested,
            action: statusKey === 'ready_to_suggest' ? 'Suggest Program'
                : statusKey === 'awaiting_response' ? 'Review Request'
                : statusKey === 'ready_to_publish' ? 'Publish'
                : statusKey === 'building_program' ? 'Update Progress'
                /*
                 * A sent suggestion waiting on the school has no primary action.
                 * The follow-up was removed from the flow (Ian, 17 Sep); View
                 * school and Contact school stay in the row menu.
                 */
                : statusKey === 'suggested' ? null
                // PG-05 — the school asked for changes to a suggestion.
                : statusKey === 'under_review' && !inbound ? 'Review Changes'
                : statusKey === 'ready_to_link' ? 'Link Program'
                : statusKey === 'completed' ? 'View School'
                // PW slide 19, "Declined · outbound" — a declined suggestion can be reopened.
                : statusKey === 'declined' && !inbound ? 'Reopen'
                : 'Contact School',
            tab,
            inbound,
            // The reason the school was given, for the Declined chip tooltip.
            declineReason: r.decline_reason || '',
            raw: r,
        };
    };

    /*
     * Only the newest request may fill the table. Switching tabs fires two
     * (the tab, then page 1), and a slower answer for the tab just left could
     * land last: its rows belong to another tab, the page filters them all out,
     * and a full tab read "No opportunities match the current filters".
     */
    const loadSeqRef = useRef(0);
    const loadRows = useCallback(async () => {
        const seq = ++loadSeqRef.current;
        setLoading(true);
        try {
            const res = await listProgramRequestsService({
                // Server-side: tab → a status set, country → a set of org_codes,
                // status → the numeric code the API stores. Filtering and paging
                // together mean a page of ten is ten matching rows.
                tab:     activeTab,
                /*
                 * The Type filter, server-side like the rest, so a page of ten is
                 * ten rows of that type. Credit has no rows yet; it asks for
                 * everything and the page filter below returns none.
                 */
                request_type: activeType === 'unlock' ? 'unlock'
                    : activeType === 'program' ? 'learning_program'
                    : activeType === 'license' ? 'licences'
                    : 'all',
                country: country !== 'all' ? country : undefined,
                // cos | lc — the unlock requests for that solution (LA-PW-19Oct-01).
                solution: solution !== 'all' ? solution : undefined,
                search:  search.trim() || undefined,
                // The chip itself: one stored code can be two chips (Built is
                // Completed or Ready to Link), so the server filters by the chip.
                status_key: status !== 'all' ? status : undefined,
                page:    page + 1,
                limit:   rowsPerPage,
            });
            if (seq !== loadSeqRef.current) return; // a newer request is on its way
            const docs = res?.data?.requests || res?.data?.docs || [];
            setRows(docs.map(toRow));
            setTotal(Number(res?.data?.total) || 0);
            setStatusCounts(res?.data?.status_counts || {});
            setApiTabCounts(res?.data?.tab_counts || null);
            // Workspace-wide, so Credit Demand survives a tab change.
            setOpenSessions(Number(res?.data?.open_sessions) || 0);
            setCountryOptions(res?.data?.country_options || []);
        } catch (e) {
            if (seq !== loadSeqRef.current) return;
            // An empty pipeline and a failed fetch look identical otherwise.
            toast(TOAST_TYPES.ERROR, 'Could not load opportunities', e?.message || 'Please try again.');
            setRows([]);
        } finally {
            if (seq === loadSeqRef.current) setLoading(false);
        }
    }, [activeTab, activeType, country, solution, search, status, page, rowsPerPage]);

    // A filter change must return to page 1 — page 3 of the old result set is
    // rarely a valid page of the new one.
    useEffect(() => { setPage(0); }, [activeTab, activeType, country, solution, search, status]);

    useEffect(() => { loadRows(); }, [loadRows]);

    /*
     * The credit balance is a property of the WORKSPACE, not of the current
     * filter, so it is fetched once on mount. It was inside loadRows, which made
     * every tab click and keystroke re-request it.
     *
     * Same endpoint Account Settings uses — the pipeline reports the partner's
     * real position rather than keeping a number of its own.
     */
    useEffect(() => {
        let cancelled = false;
        getCreditBalanceService()
            .then((bal) => {
                if (cancelled) return;
                /*
                 * balance_credits, not credit_balance.
                 *
                 * getBalance returns { dq_insider_id, balance_credits, balance_usd }.
                 * Reading the wrong key gave NaN, which fell through to null, and
                 * Credit Gap printed an em dash for every partner. Account Settings,
                 * the client list and the chat sidebar all read balance_credits.
                 */
                const balance = Number(bal?.data?.balance_credits);
                setCreditBalance(Number.isFinite(balance) ? balance : null);
            })
            .catch(() => { if (!cancelled) setCreditBalance(null); });
        return () => { cancelled = true; };
    }, []);


    const totalPages = Math.max(1, Math.ceil(total / rowsPerPage));

    /*
     * Tab counts and stat cards come from status_counts, which the API computes
     * across the WHOLE workspace and does not filter.
     *
     * They cannot be derived from : with the tab filter applied
     * server-side, rows holds one tab's page, so every other tab would read 0
     * while actually holding work.
     */
    const countOf = useCallback(
        (...codes) => codes.reduce((n, c) => n + (Number(statusCounts[String(c)]) || 0), 0),
        [statusCounts],
    );

    /*
     * Tab labels. A pending SENT suggestion waits on the school, so it lives
     * under In Progress · outbound (slide 17), not Open — status alone cannot
     * say so, which is why the API counts tabs itself (tab_counts). Grouping by
     * status put those rows in Open's label while no tab listed them.
     * The status-based fallback is only for an API without tab_counts.
     */
    const tabCounts = useMemo(() => apiTabCounts || ({
        // 10 — the datahouse drafts. Without this the tab read 0 while holding rows.
        ready_to_suggest: countOf(WORKSPACE_REQUEST_STATUS.READY_TO_SUGGEST),
        open:        countOf(0),
        in_progress: countOf(1, 2, 3),
        completed:   countOf(5),
        declined:    countOf(9),
    }), [apiTabCounts, countOf]);

    const liveStats = useMemo(() => {
        /*
         * Credit Demand — what the OPEN queue would cost to build, at slide 22's
         * rate of 30 credits per session.
         *
         * open_sessions is workspace-wide and comes from the API. It used to be
         * summed from the rows on screen, which meant the card read 0 on every
         * tab except Open (the filter matched nothing) and under-reported
         * whenever the open queue ran past the first page.
         */
        const demand = openSessions * CREDITS_PER_SESSION;

        /*
         * The gap is a SHORTFALL, so it is clamped at 0 — a partner with credits
         * to spare has no gap, and a negative number here would read as one.
         * But a bare "0" is unreadable: it looks broken rather than healthy.
         * The subtitle carries the arithmetic behind it, so 0 says "covered"
         * instead of leaving the partner guessing.
         */
        return {
            // The Open tab: requests from schools, networks and teachers that
            // wait on the partner — "Direct requests needing follow-up". It
            // used to count every pending row, the partner's own unanswered
            // suggestions included.
            'Open Opportunities': tabCounts.open,
            'Awaiting Response':  tabCounts.open,
            // 10, not 3. Status 3 is ACCEPTED — a suggestion the school has
            // already taken, which is the opposite of one waiting to be sent.
            'Ready to Suggest':   countOf(WORKSPACE_REQUEST_STATUS.READY_TO_SUGGEST),
            'Credit Demand':      demand.toLocaleString(),
            'Credit Gap':         creditBalance === null ? '—' : Math.max(0, demand - creditBalance).toLocaleString(),
            /*
             * Blocked stays unmeasured. Slide 22 defines it as "Waiting on data,
             * credits or capacity" and its own example is "not enough data to size
             * · 0 of ~600 assessed" — an assessment-coverage judgement this page
             * has no input for. A 0 would read as "nothing is blocked".
             */
            'Blocked':            '—',
        };
    }, [openSessions, creditBalance, countOf, tabCounts]);

    /**
     * Card subtitles that need live numbers; the rest fall back to STAT_CARDS.
     *
     * The deck prints this pair in BOTH of its states, and the wording differs:
     *   slide 22, short:   "1,760 Available · 790 short"
     *   slide 20, covered: "1,760 available · 620 left after this send"
     *
     * Both are reproduced, so the card always shows the subtraction rather than
     * a bare "0" — which reads as broken where "9,720 left after this send"
     * reads as healthy.
     */
    const liveDescs = useMemo(() => {
        if (creditBalance === null) return {};
        const demand = openSessions * CREDITS_PER_SESSION;
        const avail = creditBalance.toLocaleString();
        return {
            'Credit Gap': demand > creditBalance
                ? `${avail} available · ${(demand - creditBalance).toLocaleString()} short`
                : `${avail} available · ${(creditBalance - demand).toLocaleString()} left after this send`,
        };
    }, [openSessions, creditBalance]);

    const visibleGroups = useMemo(() => {
        const inTab = rows.filter((r) => r.tab === activeTab).filter((row) => {
            if (activeType !== 'all' && row.type !== activeType) return false;
            if (status !== 'all' && row.status !== status) return false;
            if (search && !`${row.org} ${row.title}`.toLowerCase().includes(search.toLowerCase())) return false;
            return true;
        });

        if (activeTab === 'open') {
            return [
                { key: 'awaiting_response', title: 'Awaiting your response', rows: inTab.filter((r) => r.status === 'awaiting_response') },
                { key: 'ready_to_suggest',  title: 'Ready to suggest',       rows: inTab.filter((r) => r.status === 'ready_to_suggest') },
            ].filter((g) => g.rows.length > 0).map((g) => ({ ...g, count: g.rows.length }));
        }

        /*
         * Its own branch, because the fall-through below titles everything
         * that is not 'completed' as 'Declined' — which filed six drafted
         * suggestions under a heading saying the school refused them.
         */
        if (activeTab === 'ready_to_suggest') {
            return inTab.length
                ? [{ key: 'ready_to_suggest', title: 'Ready to suggest', count: inTab.length, rows: inTab }]
                : [];
        }

        if (activeTab === 'in_progress') {
            return [
                // Slide 17 calls these 'inbound' / 'outbound'. Plain words here, matching
                // the school's own 'Requested by School' / 'Suggested by Partner'.
                { key: 'inbound',  title: 'In progress · requested by schools', rows: inTab.filter((r) => r.inbound) },
                { key: 'outbound', title: 'In progress · suggested by you',     rows: inTab.filter((r) => !r.inbound) },
            ].filter((g) => g.rows.length > 0).map((g) => ({ ...g, count: g.rows.length }));
        }

        return inTab.length
            ? [{ key: activeTab, title: activeTab === 'completed' ? 'Completed' : 'Declined', count: inTab.length, rows: inTab }]
            : [];
    }, [rows, activeTab, activeType, status, search]);


    const hasActiveFilters = activeType !== 'all' || status !== 'all' || country !== 'all' || solution !== 'all'
        || search.trim() !== '';

    const handleClearFilters = () => {
        setActiveType('all');
        setStatus('all');
        setCountry('all');
        setSolution('all');
        setSearch('');
    };

    return (
        <Box sx={{ width: '100%', maxWidth: '100%', px: { xs: 2, sm: 0 }, py: { xs: 3, sm: 5 } }}>
            <Box sx={{
                display: 'flex', flexDirection: { xs: 'column', md: 'row' },
                justifyContent: 'space-between', alignItems: { xs: 'flex-start', md: 'flex-end' }, gap: 2, mb: 3,
            }}>
                <Box>
                    <Typography sx={{ fontSize: { xs: '24px', sm: '30px' }, fontWeight: 800, color: TEXT, mb: 1.5 }}>
                        Opportunity Pipeline
                    </Typography>
                    <Typography sx={{ fontSize: '15px', color: MUTED, lineHeight: 1.6, }}>
                        Review requests and data-driven opportunities for upgrades, credits, dashboard expansion,
                        and learning programs.
                    </Typography>
                </Box>

                {/*
                  * Slide 19 draws two controls up here: this search and a
                  * "New opportunity" button. Only the search is restored —
                  * "New opportunity" would start an outbound suggestion, and
                  * nothing writes source = SUGGESTED yet, so the button would
                  * lead nowhere. It comes back with that flow.
                  */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
                    <TextField
                        size="small"
                        placeholder="Search schools or networks"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        sx={{
                            minWidth: '240px',
                            '& .MuiOutlinedInput-root': { borderRadius: '50px', fontSize: '13.5px', backgroundColor: 'var(--brand-surface, #ffffff)' },
                            '& .MuiOutlinedInput-notchedOutline': { borderColor: BORDER },
                        }}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchOutlinedIcon sx={{ fontSize: 18, color: MUTED }} />
                                </InputAdornment>
                            ),
                        }}
                    />
                </Box>
            </Box>

            {/* Status tabs */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3, borderBottom: `1px solid ${BORDER}` }}>
                {STATUS_TABS.map((tab) => {
                    const active = activeTab === tab.key;
                    return (
                        <Box
                            key={tab.key}
                            onClick={() => setActiveTab(tab.key)}
                            sx={{
                                display: 'flex', alignItems: 'center', gap: 0.75, cursor: 'pointer',
                                px: 1.5, py: 1.5, borderBottom: active ? `2px solid ${ORANGE}` : '2px solid transparent',
                            }}
                        >
                            <Typography sx={{ fontSize: '14px', fontWeight: active ? 800 : 600, color: active ? TEXT : MUTED }}>
                                {tab.label}
                            </Typography>
                            <Typography sx={{ fontSize: '12.5px', fontWeight: 700, color: active ? ORANGE : MUTED }}>
                                {tabCounts[tab.key] || 0}
                            </Typography>
                        </Box>
                    );
                })}
            </Box>

            {/* Stat cards */}
            <Box sx={{
                display: 'grid', gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(3, 1fr)', md: 'repeat(6, 1fr)' },
                gap: 2, mb: 4,
            }}>
                {STAT_CARDS.map((stat) => (
                    <Box key={stat.label} sx={{ ...plainCardSx, p: 2 }}>
                        <Typography sx={{ ...eyebrowSx, letterSpacing: '0.3px' }}>
                            {stat.label}
                        </Typography>
                        <Typography sx={{ fontSize: '22px', fontWeight: 800, color: TEXT, mb: 0.5 }}>
                            {liveStats[stat.label] ?? stat.value}
                        </Typography>
                        {(liveDescs[stat.label] || stat.desc) && (
                            <Typography sx={{ fontSize: '11.5px', color: MUTED, mt: 0.5, lineHeight: 1.4 }}>
                                {liveDescs[stat.label] || stat.desc}
                            </Typography>
                        )}
                    </Box>
                ))}
            </Box>

            {/* Filters */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3, flexWrap: 'wrap' }}>
                <Typography sx={{ fontSize: '13px', fontWeight: 700, color: MUTED, mr: 0.5 }}>Type</Typography>
                {TYPE_FILTERS.map((f) => {
                    const active = activeType === f.key;
                    return (
                        <Box
                            key={f.key}
                            onClick={() => setActiveType(f.key)}
                            sx={{
                                cursor: 'pointer', px: 1.75, py: 0.65, borderRadius: '50px',
                                border: `1px solid ${active ? ORANGE : BORDER}`,
                                backgroundColor: active ? 'rgba(255, 105, 0, 0.08)' : 'var(--brand-surface, #ffffff)',
                            }}
                        >
                            <Typography sx={{ fontSize: '13px', fontWeight: 700, color: active ? ORANGE : MUTED }}>
                                {f.label}
                            </Typography>
                        </Box>
                    );
                })}

                <Box sx={{ flex: 1 }} />

                <Select
                    size="small" value={status} onChange={(e) => setStatus(e.target.value)}
                    IconComponent={ArrowDropDownOutlinedIcon} sx={filterSelectSx}
                >
                    <MenuItem value="all" sx={{ fontSize: '13.5px' }}>Status</MenuItem>
                    {Object.entries(STATUS_CHIP_STYLES).map(([key, s]) => (
                        <MenuItem key={key} value={key} sx={{ fontSize: '13.5px' }}>{s.label}</MenuItem>
                    ))}
                </Select>
                <Select
                    size="small" value={country} onChange={(e) => setCountry(e.target.value)}
                    IconComponent={ArrowDropDownOutlinedIcon} sx={filterSelectSx}
                >
                    {/*
                      * Countries come from the schools this partner actually has, not
                      * a fixed list: a hard-coded Ireland/Germany/UK offered filters
                      * that match nothing for most partners, and hid the ones that do.
                      */}
                    <MenuItem value="all" sx={{ fontSize: '13.5px' }}>Country</MenuItem>
                    {countryOptions.map((c) => (
                        <MenuItem key={c} value={c} sx={{ fontSize: '13.5px' }}>{countryName(c)}</MenuItem>
                    ))}
                </Select>
                <Select
                    size="small" value={solution} onChange={(e) => setSolution(e.target.value)}
                    IconComponent={ArrowDropDownOutlinedIcon} sx={filterSelectSx}
                >
                    <MenuItem value="all" sx={{ fontSize: '13.5px' }}>Solution</MenuItem>
                    <MenuItem value="cos" sx={{ fontSize: '13.5px' }}>COS Children Assessment</MenuItem>
                    <MenuItem value="lc" sx={{ fontSize: '13.5px' }}>LearnCreator</MenuItem>
                </Select>

                {hasActiveFilters && (
                    <Typography
                        onClick={handleClearFilters}
                        sx={{ fontSize: '13px', fontWeight: 700, color: ORANGE, cursor: 'pointer', whiteSpace: 'nowrap' }}
                    >
                        Clear filters
                    </Typography>
                )}
            </Box>

            {/* Groups */}
            {/* Same loader the request inbox uses — a filter refetch should not
                leave the old rows on screen looking like the new result. */}
            {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Box>
            ) : visibleGroups.length > 0 ? (
                visibleGroups.map((group) => (
                    <OpportunityGroup key={group.key} group={group}
                                     onActionClick={handleActionClick} onMenuAction={handleMenuAction} />
                ))
            ) : (
                <Box sx={{ ...plainCardSx, p: 4, textAlign: 'center' }}>
                    <Typography sx={{ fontSize: '13.5px', color: MUTED }}>
                        No opportunities match the current filters.
                    </Typography>
                </Box>
            )}

            {/* Server-side pagination — same pager as the request inbox. */}
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 3, gap: 1, flexWrap: 'wrap' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography sx={{ fontSize: 12.5, color: MUTED }}>Rows per page:</Typography>
                    <TextField select size="small" value={rowsPerPage}
                        onChange={(e) => { setRowsPerPage(Number(e.target.value)); setPage(0); }}
                        sx={{ width: 76, backgroundColor: 'var(--brand-surface, #fff)' }}>
                        {PAGE_SIZE_OPTIONS.map((n) => <MenuItem key={n} value={n}>{n}</MenuItem>)}
                    </TextField>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography sx={{ fontSize: 12.5, color: MUTED }}>{`Page ${page + 1} of ${totalPages}`}</Typography>
                    <Button size="small" variant="outlined" sx={navBtnSx}
                            disabled={loading || page <= 0}
                            onClick={() => setPage((p) => Math.max(0, p - 1))}>Previous</Button>
                    <Button size="small" variant="outlined" sx={navBtnSx}
                            disabled={loading || page + 1 >= totalPages}
                            onClick={() => setPage((p) => p + 1)}>Next</Button>
                </Box>
            </Box>

            <SuggestProgramDialog
                open={suggestDialogOpen}
                row={suggestRow}
                drafts={schoolDrafts}
                checkedIds={checkedDraftIds}
                onToggle={toggleDraft}
                cohorts={cohortById}
                onEditCohort={(d) => setCohortEditRow(d)}
                plans={planById}
                onPlanChange={setDraftPlan}
                loadingDrafts={loadingDrafts}
                creditBalance={creditBalance}
                sending={sendingSuggestion}
                onClose={() => setSuggestDialogOpen(false)}
                onSend={handleSendSuggestion}
            />
            <EditCohortDialog
                open={Boolean(cohortEditRow)}
                schoolName={cohortEditRow?.org}
                topic={cohortEditRow?.raw?.topic}
                initial={cohortEditRow ? (cohortById.get(cohortEditRow.id) || cohortEditRow.raw) : null}
                grades={cohortChoices.grades}
                classes={cohortChoices.classes}
                onClose={() => setCohortEditRow(null)}
                onSave={(cohort) => { setDraftCohort(cohortEditRow.id, cohort); setCohortEditRow(null); }}
            />
            <ReviewChangesDialog
                readOnly
                open={Boolean(changesViewRow)}
                row={changesViewRow}
                onClose={() => setChangesViewRow(null)}
                onContinue={() => {
                    const raw = changesViewRow?.raw;
                    setChangesViewRow(null);
                    if (raw) resumeBuild(raw);
                }}
            />
            <ReviewChangesDialog
                open={Boolean(changesRow)}
                row={changesRow}
                grades={cohortChoices.grades}
                classes={cohortChoices.classes}
                sending={sendingBack}
                onClose={() => setChangesRow(null)}
                onSendBack={handleSendBack}
            />
            <SuggestionSentDialog
                schoolName={suggestRow?.org}
                count={sentCount}
                open={suggestionSentOpen}
                onClose={() => setSuggestionSentOpen(false)}
            />
            <ReviewBuildRequestDialog
                open={reviewDialogOpen}
                row={reviewRow}
                onClose={() => setReviewDialogOpen(false)}
                onAccept={handleAcceptRequest}
                onDecline={handleDeclineFromReview}
            />
            <DeclineReasonDialog
                open={declineDialogOpen}
                row={reviewRow}
                onKeepRequest={handleKeepRequest}
                onConfirmDecline={handleConfirmDecline}
            />
            <ApproveUnlockDialog
                open={!!approveRow}
                row={approveRow}
                approving={approving}
                onCancel={() => setApproveRow(null)}
                onApprove={handleApproveUnlock}
            />
            <ApproveLicencesDialog
                open={!!approveLicenceRow}
                row={approveLicenceRow}
                approving={approving}
                onCancel={() => setApproveLicenceRow(null)}
                onApprove={handleApproveLicences}
            />

            {/*
              * The same publish dialog the Learning Programs list and the program
              * dashboard use. deliveredSchoolName switches the success copy to the
              * "linked to (school)" wording, which is the case here: this publish
              * fulfils that school request.
              */}
            <PublishProgramDialogs
                stage={publishDialog?.stage}
                publishing={!!publishingId}
                onGoBack={() => setPublishDialog(null)}
                onConfirmPublish={publishForRow}
                deliveredSchoolName={publishDialog?.row?.org || ''}
                onGoToMaterials={() => {
                    const code = publishDialog?.row?.raw?.program_code;
                    setPublishDialog(null);
                    if (code) history.push(getProgramPreviewPath(code));
                }}
                onLinkToSchool={() => {
                    // Publishing already delivered it to the requesting school,
                    // so there is nothing further to link — close and let the
                    // refreshed row show it as delivered.
                    setPublishDialog(null);
                }}
            />
        </Box>
    );
};

export default OpportunityPipelinePage;
